import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from '@mui/material';
import addBlockIcon from '@src/assets/icons/add-block.svg';
import CrownImg from '@src/assets/images/crown.png';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import useStableCallback from '@src/hooks/useStableCallback';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  CAPTION_PANEL_WIDTH,
  DUBBING_PANEL_VIEW,
} from '@src/constants/dubbing';
import { HISTORY_SCOPE, HISTORY_LABEL } from '@src/constants/history';
import { LANDING_PAGE_URL } from '@src/configs';
import apis from '@src/apis';
import {
  registerRequestMeta,
  trackConvertSuccess,
} from '@src/services/moengage';
import {
  pickSubmittedVoice,
  trackTryVoice,
  TRY_VOICE_ACTION,
} from '@src/services/voiceTracking';
import {
  MOE_MODULE,
  MOE_PAYWALL_TRIGGER,
  MOE_PAYWALL_TYPE,
  MOE_SURFACE,
} from '@src/constants/moengage';
import usePaywallTracking from '@src/hooks/usePaywallTracking';
import {
  SIDEBAR_WIDTH_COLLAPSED,
  FLASH_SALE_BANNER_HEIGHT,
} from '@src/styles/config';
import {
  CONVERTING_STATUS,
  DIRECTION,
  MAX_CHARACTER_PER_BLOCK_SRT,
  HEADER_HEIGHT,
  AUDIO_PLAYER_HEIGHT,
  CHARACTER_LIMIT_BANNER_HEIGHT,
  EXPORT_BANNER_HEIGHT,
  FETCH_BLOCKS_STATUS_INTERVAL,
  FETCH_TRANSLATE_STATUS_INTERVAL,
  BLOCK_STATUS,
  BLOCK_MARGIN,
  MAX_TOTAL_CHARACTERS,
  PROJECT_PRODUCT,
  TRANSLATION_STATUS,
  REWRITE_STATUS,
  TRANSLATE_WARNING_TYPE,
  AUTO_SAVE_STATUS,
  SYNTHESIS_CONTEXT,
} from '@src/constants/project';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { useHistory } from 'react-router-dom';
import {
  updateProject,
  getTranslateProgress,
  getRewriteProgress,
} from '@src/apis/project';
import { checkFeaturePermission, countTextLength } from '@src/services/tts';
import {
  DEFAULT_SPEED,
  VOICE_OWNERSHIP,
  // OUTPUT_AUDIO,
} from '@src/constants/voice';
import actions from '@src/redux/actions';
import { ERROR_CODE } from '@src/errors/code';
import { useAudioPlayerDubbingProject } from '@src/hooks/useAudioPlayerDubbingProject';
import { useVideoDubbingAudioPlayer } from '@src/hooks/useVideoDubbingAudioPlayer';
import { useMusicPlayback } from '@src/hooks/useMusicPlayback';
import useUnloadGuard from '@src/hooks/useUnloadGuard';
import useAutoSaveFlush from '@src/hooks/useAutoSaveFlush';
import useIsAutoSaveOverLimit from '@src/hooks/useIsAutoSaveOverLimit';
import CommonButton from '@src/new-components/Button';
import AILoadingOverlay from '@src/new-components/AILoadingOverlay';
import TurnstileWidget from '@src/components/TurnstileWidget';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_LEVEL_V3,
  PACKAGE_STUDIO_VERSION,
} from '@src/constants/package';
import {
  checkCustomPackage,
  checkShouldNotifyLowCredits,
} from '@src/services/package';
import OutOfCreditsCustom from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsCustom';
import OutOfCreditsVip from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsVip';
import OutOfCreditsFree from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsFree';
import useUserInfo from '@src/hooks/useTtsUser';
import { DIALOG_V2_TYPE, CAPTCHA_PROVIDER } from '@src/constants';
import FreeMaxCharsLimitExceeded from '@src/containers/TTSNew/MaxCharsLimitExceeded/FreeMaxCharsLimitExceeded';
import PaidMaxCharsLimitExceeded from '@src/containers/TTSNew/MaxCharsLimitExceeded/PaidMaxCharsLimitExceeded';
import WaringImg from '@src/assets/images/warning.png';
import { delimitNumber } from '@src/utils/number';
import NotificationDialogV4 from '@src/components/NotificationDialogV4';
import sanitizeHtml from 'sanitize-html';
import LockCycle from '@src/assets/images/lock-cycle.svg';
import ROUTES from '@src/constants/route';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import ExpiredPackageAlert from '@src/containers/TTSNew/ExpiredPackageAlert';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import OutOfCreditsStandard from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsStandard';
import OutOfCreditsSpecial from '@src/containers/TTSNew/OutOfCreditsAlert/OutOfCreditsSpecial';
import useRequestAnimationStatus from '@src/hooks/useRequestAnimationStatus';
import useSynthesisValidation from '@src/hooks/useSynthesisValidation';
import NotificationDialog from '@src/components/NotificationDialog';
import {
  invalidateBlockAudio,
  audioExpired,
  buildProjectPayload,
  buildVideoSettingsPayload,
  checkBlocksChanged,
  isBlockChanged,
  getBlockWithAudio,
  getBlockStatus,
  resolveBlockVoice,
  buildInsertedBlockTiming,
  checkBlockTextError,
  isVideoSettingsChanged,
  isVideoSource,
  getSourceUrl,
  isBlockScopedLoading,
  getExportingState,
  isVideoExportPending,
  getAudioAdjustedBlockEndTime,
  resolveCreateAudioAction,
  pickConvertibleBlocks,
  isBlockRewriting,
  buildBlockSynthesisFailureUpdates,
  resolvePolledBlockSynthesis,
  calculateBlocksSynthesisCredits,
} from '@src/services/project';
import { loadAudioDurationFromUrl } from '@src/services/audio';
import { containsBlacklistedWords } from '@src/services/blackword';
import { pickChangedBlocksContent } from '@src/services/autoSave';
import {
  buildUpdatedTrialUsage,
  calculateVoiceCreditsMap,
  findExceededTrialVoices,
  isUsingTrialVoice,
  willExceedTrialLimit,
} from '@src/services/voiceTrial';
import { COLOR } from '@src/styles/color';
import NotifyLowCredits from '@src/containers/TTSNew/NotifyLowCredits';
import useTotalCredits from '@src/hooks/useTotalCredits';
import { getUpgradePackageLabelKey } from '@src/services/voice';
import useVideoPlaybackSync from '@src/hooks/useVideoPlaybackSync';
import { TIMELINE_PANEL_HEIGHT } from '@src/constants/timeline';
import { VIDEO_DUBBING_MAX_DURATION_SECONDS } from '@src/constants/transcript';
import { canAppendEndBlock, getBlocksMaxEndSec } from '@src/services/timeline';
import PopupUpPkgUseRewrite from './PopupUpPkgUseRewrite';
import AudioPlayer from '../../component/AudioPlayer';
import CustomActionLeavePage from '../../component/CustomActionLeavePage';
import AutoSaveBanner from '../../component/AutoSaveBanner';
import ExportingBanner from '../../component/ExportingBanner';
import TranslateDialog from '../../component/TranslateDialog';
import { StyledContent } from './index.style';
import VirtualizedContentList from './VirtualizedContentList';
import VideoPreviewPanel from './VideoPreviewPanel';
import CaptionPanel from './CaptionPanel';
import DubbingTimeline from '../Timeline';
import Toolbar from '../Toolbar';
import { StyledActionWarning } from '../../component/index.style';
import PopupNotEnoughCredits from './PopupNotEnoughCredits';
import PopupConfirmCreateAudio from './PopupConfirmCreateAudio';

const Content = ({ isExtracting = false, extractProgressEnabled = false }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const addBlockRef = useRef({});
  const dispatch = useDispatch();
  const { openSidebar, dubbingPanelView } = useSelector(
    (state) => state.layout,
  );
  const { headerVoucher } = useSelector((state) => state.voucher);
  const { synthesisRequest } = useSelector((state) => state.synthesisRequest);

  const user = useSelector((state) => state.auth.user);
  const {
    multiBlock,
    projectId,
    title,
    voiceDefault,
    currentBlockId,
    isDownloading,
    isProjectSynthesizing,
    isProjectConvertingDraft,
    originalTitle,
    isPlayingBlock,
    currentPlayingBlockId,
    openDialogChangeFormat,
    audioType,
    originalAudioType,
    currentFilteringStatus,
    startFilterBlock,
    selectedBlockIds,
    currentFilteringSpeakerId,
    audioLinkSrt,
    audioSrtExpiredAt,
    isPreProcessingSynthesis,
    translation,
    isTranslationPendingSynthesis,
    source,
    videoSettings,
    originalVideoSettings,
    product,
  } = useSelector((state) => state.project);
  const { usingPackage, trialVoiceUsage: stateTrialVoiceUsage } = useSelector(
    (state) => state.user,
  );
  const allPackages = useSelector((state) => state.packages.packages);
  const { ttsUser } = useUserInfo();
  const { totalCredits } = useTotalCredits();
  const totalCreditsRef = useRef(totalCredits);
  useEffect(() => {
    totalCreditsRef.current = totalCredits || 0;
  }, [totalCredits]);
  const turnstileWidgetRef = useRef(null);

  const { blocks, blockIds, originalBlocks, originalBlockIds } = multiBlock;
  const isChangedTitle = originalTitle !== title;
  const isChangedAudioType = originalAudioType !== audioType;
  const hasVideoSettingsChanged = isVideoSettingsChanged(
    videoSettings,
    originalVideoSettings,
  );
  const hasBlockChanged = checkBlocksChanged({
    blocks,
    originalBlocks,
    blockIds,
    originalBlockIds,
  });
  const isDraft =
    hasBlockChanged ||
    isChangedTitle ||
    isChangedAudioType ||
    hasVideoSettingsChanged;
  const blockIdsRef = useRef(blockIds);
  const blocksRef = useRef(blocks);
  const timeoutBackgroundColor = useRef(null);
  const pendingBlockAdditions = useRef(new Set());
  const currentFilteringStatusRef = useRef(currentFilteringStatus);
  const currentFilteringSpeakerIdRef = useRef(currentFilteringSpeakerId);
  const synthesisRequestRef = useRef(synthesisRequest);

  const [characterExceed, setCharacterExceed] = useState(false);
  const [focusingBlockId, setFocusingBlockId] = useState(null);
  const [openOutOfCreditsDialog, setOpenOutOfCreditsDialog] = useState(false);
  const [warningOneTimeCredits, setWarningOneTimeCredits] = useState(false);
  const [openWrongSyntaxDialogV2, setOpenWrongSyntaxDialogV2] = useState(null);
  const [charsFactorCheckbox, setCharsFactorCheckbox] = useState(false);
  const [triggerBtn, setTriggerBtn] = useState(false);
  const [openWrongSyntaxDialog, setOpenWrongSyntaxDialog] = useState(null);
  const [openExpiredPackageDialog, setOpenExpiredPackageDialog] =
    useState(false);
  const [openMaxCharsLimitExceeded, setOpenMaxCharsLimitExceeded] =
    useState(false);
  const [openMaxCharsExceededOnSave, setOpenMaxCharsExceededOnSave] =
    useState(false);
  const [openDialogLockedCycle, setOpenDialogLockedCycle] = useState(null);
  const [scrollToBlockId, setScrollToBlockId] = useState(null);
  const [scrollToBlockAlign, setScrollToBlockAlign] = useState('center');
  // Separate from the block-list request above: bringing a block into view in
  // the editor does not always mean the voice track should move too.
  const [revealClipId, setRevealClipId] = useState(null);
  const [filteredBlockIds, setFilteredBlockIds] = useState(blockIds);
  const [filterBlockResults, setFilterBlockResults] = useState(blocks);
  const [deleteDialogData, setDeleteDialogData] = useState(false);
  const [notifyLowCreditsDialog, setNotifyLowCreditsDialog] = useState(false);
  const [openTranslateDialog, setOpenTranslateDialog] = useState(false);
  const [openTranslateConfirmDialog, setOpenTranslateConfirmDialog] =
    useState(false);
  const [translateWarningType, setTranslateWarningType] = useState(null);
  const [openTranslateErrorDialog, setOpenTranslateErrorDialog] =
    useState(false);
  const [translateErrorDescription, setTranslateErrorDescription] =
    useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [openPopupUpPkgUseRewrite, setOpenPopupUpPkgUseRewrite] =
    useState(false);
  const translationIdRef = useRef(null);
  const [blackWordsList, setBlackWordsList] = useState([]);
  const [blockBlackWordsMap, setBlockBlackWordsMap] = useState({});
  const [openPopupNotEnoughCredits, setOpenPopupNotEnoughCredits] =
    useState(false);
  // True only while a WHOLE-PROJECT convert is running (create-audio flow), so
  // the AudioPlayer shows the % progress like TTS's `isProjectSynthesizing`.
  // A single-block convert must NOT flip this (button stays unchanged).
  const [isConvertingAllBlocks, setIsConvertingAllBlocks] = useState(false);

  const packageCode = usingPackage?.code;
  const { trackHit: trackPaywall } = usePaywallTracking({
    module: MOE_MODULE.PROJECT,
    product: PROJECT_PRODUCT.DUBBING,
    projectId,
    trigger: MOE_PAYWALL_TRIGGER.CONVERT,
  });
  const maxLengthInputText = ttsUser?.maxLengthInputText || 0;

  const { getFeatureValue } = useFeatureFlags();

  // Timeline (AIV-12773) — only for a DUBBING project whose source is a video
  // (same product/source guard as create-audio), gated behind the flag. Local
  // `vbee.timelinePreview` in localStorage lets devs preview it while the flag
  // is still off; remove that fallback before release.
  const timelineTracksEnabled =
    getFeatureValue(FEATURE_KEYS.VIDEO_DUBBING_TIMELINE_TRACKS, {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    }) &&
    product === PROJECT_PRODUCT.DUBBING &&
    isVideoSource(source);

  const createAudioEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_CREATE_AUDIO,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  // Only apply the new per-block create-audio flow to a DUBBING project whose
  // source is a video (source.type === VIDEO), gated behind the flag.
  const videoRef = useRef(null);
  const canCreateBlockAudio =
    createAudioEnabled &&
    product === PROJECT_PRODUCT.DUBBING &&
    isVideoSource(source);

  // Legacy single-audio engine and the new per-block/video engine are both
  // mounted but mutually exclusive via `enabled`; pick the active one's API.
  const legacyAudioPlayer = useAudioPlayerDubbingProject({
    enabled: !canCreateBlockAudio,
  });
  const videoAudioPlayer = useVideoDubbingAudioPlayer({
    enabled: canCreateBlockAudio,
    videoRef,
    // The timeline reads the moving time itself via `getCurrentSec`; the
    // AudioPlayer below renders it from state for its seekbar, so only skip the
    // state updates when the timeline is what's rendered.
    liveTimeOnly: timelineTracksEnabled,
  });
  const {
    audioRef,
    handlePlay,
    handlePause,
    handleSeeked,
    seekToBlock,
    isLoading,
    isPlaying,
    currentTime,
    getCurrentSec,
    duration,
    handleChangeVolume,
    handlePlayAudioInBlock,
    handleSeekedInBlock,
    blockAudioCurrentTime,
    blockAudioDuration,
    // Past the last frame of the video the preview shows the project's
    // background colour instead of the video's frozen final frame — the voice or
    // the bgm can run longer than the video. Only the new engine drives a video,
    // so the legacy player leaves this undefined.
    isBeyondVideo,
  } = canCreateBlockAudio ? videoAudioPlayer : legacyAudioPlayer;

  // Play the background-music track alongside the voice, following the same clock.
  useMusicPlayback({
    enabled: canCreateBlockAudio && timelineTracksEnabled,
    isPlaying,
    getCurrentSec,
  });

  const { validateSynthesis } = useSynthesisValidation();
  const { displayProgress } = useRequestAnimationStatus(synthesisRequest);

  const [isOpenUnsavedChangesDialog, setIsOpenUnsavedChangesDialog] =
    useState(false);

  const voiceTrialConfig = getFeatureValue(FEATURE_KEYS.TRIAL_VOICE_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
    packageCode: user.packageCode,
  });
  const enableVoiceTrial = Boolean(voiceTrialConfig);

  const captchaProvider = getFeatureValue(
    FEATURE_KEYS.CAPTCHA_PROVIDER_AIVOICE,
    { userId: user.id, email: user.email },
  );

  const cloudflareTurnstileEnabled = getFeatureValue(
    FEATURE_KEYS.CLOUDFLARE_TURNSTILE_AIVOICE,
    { userId: user.id, email: user.email },
  );

  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );

  const enableCreditUsagePreview = getFeatureValue(
    FEATURE_KEYS.ENABLE_CREDIT_USAGE_PREVIEW,
    { userId: user.id, email: user.email },
  );
  const isBlockUIImprovement = getFeatureValue(
    FEATURE_KEYS.PROJECT_TTS_IMPROVE_BLOCK_UI,
    { userId: user.id, email: user.email },
  );

  const autoSaveEnabled = getFeatureValue(FEATURE_KEYS.PROJECT_AUTO_SAVE, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });
  const autoSaveStatus = useSelector((state) => state.project.autoSaveStatus);

  useEffect(() => {
    dispatch(actions.project.setAutoSaveEnabled(autoSaveEnabled));
    return () => dispatch(actions.project.setAutoSaveEnabled(false));
  }, [autoSaveEnabled]);

  // Warn before closing/reloading the tab while a save is in flight or failed.
  useUnloadGuard({ enabled: autoSaveEnabled });
  const { flushAndWaitForSave } = useAutoSaveFlush();
  // Banner is shown (and reserves layout height) when over the limit.
  const isAutoSaveOverLimit = useIsAutoSaveOverLimit();

  // True while leaving the project could lose unsaved work. Under auto-save the
  // store is the source of truth (SAVING/ERROR); otherwise fall back to the
  // legacy draft detection.
  const isAutoSaveInFlight = [
    AUTO_SAVE_STATUS.SAVING,
    AUTO_SAVE_STATUS.ERROR,
  ].includes(autoSaveStatus);
  const hasUnsavedWork = autoSaveEnabled ? isAutoSaveInFlight : isDraft;

  const exports = useSelector((state) => state.project.exports);
  const videoDubbingDownloadAudioEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_DOWNLOAD_AUDIO,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );
  const videoDubbingExportVideoEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_EXPORT_VIDEO,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );
  // The mp4 export is offered under its own flag (see VideoDubbingDownloadButton),
  // so the banner has to follow it too — otherwise a render started from the
  // download menu would leave the project editable while it runs.
  const exportHistoryEnabled = useExportHistoryEnabled();
  const exportBannerEnabled =
    getFeatureValue(FEATURE_KEYS.PRESENTATION_EXPORT_VIDEO, {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    }) ||
    videoDubbingDownloadAudioEnabled ||
    videoDubbingExportVideoEnabled;
  const { isExporting, formats: exportFormats } = getExportingState({
    exports,
    isDownloading,
    product: PROJECT_PRODUCT.DUBBING,
  });
  const isAutoSaveBannerVisible =
    autoSaveEnabled &&
    (isAutoSaveOverLimit || autoSaveStatus === AUTO_SAVE_STATUS.DELETED);
  // The banner only exists to explain why editing is blocked, so it goes with
  // the lock: no lock, nothing to explain. `showExportBanner` also reserves the
  // banner's height, so gating it here keeps the layout from leaving a gap.
  const showExportBanner =
    exportBannerEnabled &&
    isExporting &&
    !isAutoSaveBannerVisible &&
    !exportHistoryEnabled;
  // Disable all interactions while transcribe-chunk streaming is in progress.
  const isSkeletonUI = extractProgressEnabled && isExtracting;
  // Exporting no longer locks the project. The backend snapshots the project
  // when the export starts, so edits made while it renders cannot change the
  // file. The banner still reports the export; only the lock is gone. Behind
  // the export-history flag, so the previous behaviour is one switch away.
  const isEditLocked =
    isDownloading ||
    (exportBannerEnabled && isExporting && !exportHistoryEnabled) ||
    isSkeletonUI;
  // Only the mp4 render bakes the video settings into its output, so they are
  // frozen for that export alone — an audio/SRT download leaves them editable.
  const isVideoExporting =
    videoDubbingExportVideoEnabled && isVideoExportPending(exports);
  // Extraction loading inside the block list instead of over the whole editor
  // (Dubbing/index stands its full-screen overlay down for the same case). Not
  // for the streaming flow: there the rows arrive one batch at a time and show
  // their own skeleton, so a blur over the list would hide that progress.
  const showBlockScopedExtractLoading =
    isExtracting &&
    !extractProgressEnabled &&
    isBlockScopedLoading({ source, timelineTracksEnabled });

  const rewriteEnabled = getFeatureValue(FEATURE_KEYS.AI_REWRITE, {
    userId: user?.id,
    email: user?.email,
  });
  const videoDubbingPreviewEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_PREVIEW,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  // One flag for the whole subtitle feature, and only for a DUBBING project
  // whose source is a video — the same guard the timeline and block audio use.
  const captionEnabled =
    Boolean(
      getFeatureValue(FEATURE_KEYS.VIDEO_DUBBING_CAPTION, {
        userId: user?.id,
        email: user?.email,
        phoneNumber: user?.phoneNumber,
      }),
    ) &&
    product === PROJECT_PRODUCT.DUBBING &&
    isVideoSource(source);
  // Flag off = always the block list, whatever the store holds.
  const isCaptionPanelOpen =
    captionEnabled && dubbingPanelView !== DUBBING_PANEL_VIEW.BLOCK_LIST;

  // Mở phụ đề thì cột trái về bề rộng thiết kế, đóng lại trả về như cũ.
  const contentRef = useRef(null);
  const blockPanelRef = useRef(null);
  const blockPanelSizeRef = useRef(null);
  useEffect(() => {
    const panel = blockPanelRef.current;
    const container = contentRef.current;
    if (!panel || !container) return;
    if (isCaptionPanelOpen) {
      const totalWidth = container.clientWidth;
      if (!totalWidth) return;
      // Chỉ nhớ bề rộng gốc lần đầu, đổi qua lại giữa 2 màn không ghi đè nó.
      if (blockPanelSizeRef.current == null)
        blockPanelSizeRef.current = panel.getSize();
      panel.resize((CAPTION_PANEL_WIDTH / totalWidth) * 100);
    } else if (blockPanelSizeRef.current != null) {
      panel.resize(blockPanelSizeRef.current);
      blockPanelSizeRef.current = null;
    }
  }, [isCaptionPanelOpen]);

  // Layout state outlives the editor, so the next project must start clean.
  useEffect(
    () => () => {
      dispatch(
        actions.layout.setDubbingPanelView(DUBBING_PANEL_VIEW.BLOCK_LIST),
      );
    },
    [dispatch, projectId],
  );
  const videoDubbingAspectRatioEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_ASPECT_RATIO,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const downloadAudioEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_DOWNLOAD_AUDIO,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  // Per-block audio download is available only for a DUBBING
  // project whose source is a video, gated behind its own flag. Off = legacy
  // behavior (download option hidden for dubbing blocks).
  const canDownloadBlockAudio =
    downloadAudioEnabled &&
    product === PROJECT_PRODUCT.DUBBING &&
    isVideoSource(source);
  const videoDubbingBgColorEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_BG_COLOR,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  // Tell the auto-save engine which videoSettings fields to persist (it can't
  // read feature flags itself) so UPDATE_CONFIG sends the right Backend shape.
  useEffect(() => {
    dispatch(
      actions.project.setAutoSaveVideoConfig({
        aspectRatioEnabled: Boolean(videoDubbingAspectRatioEnabled),
        backgroundColorEnabled: Boolean(videoDubbingBgColorEnabled),
        captionEnabled,
      }),
    );
  }, [
    videoDubbingAspectRatioEnabled,
    videoDubbingBgColorEnabled,
    captionEnabled,
  ]);

  const handleAspectRatioChange = useCallback(
    (aspectRatio) => {
      dispatch(
        actions.project.updateVideoSettings({
          aspectRatio,
        }),
      );
    },
    [dispatch],
  );
  const handleBackgroundColorChange = useCallback(
    (backgroundColor) => {
      if (timeoutBackgroundColor.current) {
        clearTimeout(timeoutBackgroundColor.current);
      }
      timeoutBackgroundColor.current = setTimeout(() => {
        dispatch(actions.project.updateVideoSettings({ backgroundColor }));
      }, 150);
    },
    [dispatch],
  );
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const handleToggleVideoExpand = useCallback(() => {
    setIsVideoExpanded((prev) => !prev);
  }, []);
  const shouldShowVideoPanel =
    isVideoSource(source) && videoDubbingPreviewEnabled;
  const videoUrl = getSourceUrl(source);
  useVideoPlaybackSync({
    enabled: shouldShowVideoPanel && !canCreateBlockAudio,
    videoRef,
    audioRef,
    isPlaying,
    audioCurrentTime: currentTime,
  });

  const maxCharLimitPerBlock = MAX_CHARACTER_PER_BLOCK_SRT;
  const handleCloseExpiredPackageDialog = () => {
    setOpenExpiredPackageDialog(false);
  };
  const openLimitedFeatureDialog = (
    featureKey,
    paywallType,
    paywallVoiceCode,
  ) => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t(featureKey) }),
        paywall: {
          type: paywallType,
          voiceCode: paywallVoiceCode,
          featureKey,
        },
      }),
    );
  };

  const hasTranslatePermission = checkFeaturePermission(
    ttsUser?.features || [],
    FEATURE_KEYS.AI_TRANSLATE,
  );

  const speakers = useSelector((state) => state.project.speakers);

  const speakerConfigEnabled = getFeatureValue(FEATURE_KEYS.SPEAKER_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  useEffect(() => {
    const fetchSpeakers = async () => {
      if (!speakerConfigEnabled || !projectId) return;
      const response = await apis.project.getSpeakers(projectId);
      if (response?.status) {
        dispatch(actions.project.setSpeakers(response?.result?.speakers || []));
      }
    };
    if (projectId && speakerConfigEnabled) {
      fetchSpeakers();
    }
  }, [projectId, dispatch, speakerConfigEnabled]);

  const handleOpenVoiceTrialLimitDialog = (limitedVoices) => {
    const voices = (
      Array.isArray(limitedVoices) ? limitedVoices : [limitedVoices]
    ).filter(Boolean);

    if (!voices.length) return;

    const isMultipleVoice = voices.length > 1;

    const packageName = t(
      getUpgradePackageLabelKey({
        voices,
        userFeatures: ttsUser?.features,
        packages: allPackages,
      }),
    );

    // Find blocks using these voices to get speaker information
    const voiceCodes = voices.map((voice) => voice?.code).filter(Boolean);
    const blocksWithVoices = Object.values(blocks).filter(
      (block) => block?.voiceCode && voiceCodes.includes(block.voiceCode),
    );

    // Build voice list with speaker information
    const voiceNamesList = voices
      .map((voice) => {
        // Find blocks using this voice
        const blocksUsingVoice = blocksWithVoices.filter(
          (block) => block.voiceCode === voice?.code,
        );

        // Get unique speakers for this voice
        const speakerIds = [
          ...new Set(
            blocksUsingVoice.map((block) => block?.speakerId).filter(Boolean),
          ),
        ];

        // Get speaker names
        const speakerNames = speakerIds
          .map((speakerId) => {
            const speaker = speakers?.find((s) => s.id === speakerId);
            return speaker?.name || '';
          })
          .filter(Boolean);

        const voiceName = voice?.name || '';
        const sanitizedVoiceName = sanitizeHtml(voiceName);

        // Helper function to format voice text with optional speaker info
        const formatVoiceText = (withSpeaker) => {
          // For multiple voices, always use <b> tags in <li>
          // For single voice, don't use <b> tags (translation string adds them)
          const voiceNameFormatted = isMultipleVoice
            ? `<b>${sanitizedVoiceName}</b>`
            : sanitizedVoiceName;

          if (!withSpeaker) {
            return isMultipleVoice
              ? `<li>${voiceNameFormatted}</li>`
              : voiceNameFormatted;
          }

          const speakerLabel = t('speaker');
          const speakerText = sanitizeHtml(speakerNames.join(', '));
          const speakerInfo = `${speakerLabel}: <i>${speakerText}</i>`;
          const fullText = `${voiceNameFormatted} (${speakerInfo})`;

          return isMultipleVoice ? `<li>${fullText}</li>` : fullText;
        };

        return formatVoiceText(speakerNames.length > 0);
      })
      .filter(Boolean)
      .join('');

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: isMultipleVoice
          ? t('trialVoiceLimitDescriptionMultiple', {
              voiceNames: voiceNamesList,
              packageName,
            })
          : t('trialVoiceLimitDescription', {
              voiceName: voiceNamesList,
              packageName,
            }),
        img: CrownImg,
        classNameImg: 'custom-img',
        maxWidth: '640px',
        paywall: {
          type: MOE_PAYWALL_TYPE.VOICE_TRIAL_LIMIT,
          voiceCode: voices
            .map((v) => v?.code)
            .filter(Boolean)
            .join(','),
        },
        onUpgrade: () => {
          history.push(ROUTES.PAYMENT);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );
  };

  const handleCloseMaxCharsLimitExceededDialog = () =>
    setOpenMaxCharsLimitExceeded(false);
  const isShowHeaderBanner = headerVoucher && headerVoucher.length > 0;
  const isTranslationSuccess =
    translation?.status === TRANSLATION_STATUS.SUCCESS;

  const blockWithText = Object.keys(blocks)
    .map((blockId) => blocks[blockId])
    .filter((block) =>
      isTranslationSuccess ? block?.translatedText : block?.text,
    );

  const blockWithTextChanged = useMemo(
    () =>
      blockWithText.filter((block) => {
        const currentBlock = {
          text: isTranslationSuccess ? block?.translatedText : block?.text,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          speakerId: block?.speakerId,
          // In the video create-audio flow each block's audio is synthesized
          // independently of the timeline, so a start/end-time change is only a
          // trim and must NOT mark the block "changed" (which would disable the
          // play button and re-enable convert). Legacy SRT dubbing keeps
          // timestamps because there the audio is tied to the subtitle timing.
          ...(canCreateBlockAudio
            ? {}
            : { startTime: block?.startTime, endTime: block?.endTime }),
        };
        const originalBlock = {
          text: isTranslationSuccess
            ? originalBlocks[block.id]?.translatedText
            : originalBlocks[block.id]?.text,
          voiceCode: originalBlocks[block.id]?.voiceCode,
          speed: originalBlocks[block.id]?.speed,
          speakerId: originalBlocks[block.id]?.speakerId,
          ...(canCreateBlockAudio
            ? {}
            : {
                startTime: originalBlocks[block.id]?.startTime,
                endTime: originalBlocks[block.id]?.endTime,
              }),
        };
        return isBlockChanged(currentBlock, originalBlock);
      }),
    [
      blockWithText,
      originalBlocks,
      audioType,
      isTranslationSuccess,
      canCreateBlockAudio,
    ],
  );

  const hasAudioExpired = audioExpired(audioSrtExpiredAt);
  const hasProjectAudio = Boolean(audioLinkSrt) && !hasAudioExpired;

  // ---- New per-block create-audio flow: whole-project convert button state ----
  // Blocks that still need audio (text changed vs. original, no audioLink yet,
  // or existing audio expired). Shared by the whole-project convert handler and
  // the button's disabled/progress state so they stay in sync (mirrors TTS
  // blockWithTextChanged).
  const dubbingBlocksNeedingAudio = useMemo(
    () =>
      blockWithText.filter((block) => {
        const currentBlock = {
          text: isTranslationSuccess ? block?.translatedText : block?.text,
          voiceCode: block?.voiceCode,
          speed: block?.speed,
          speakerId: block?.speakerId,
          // Timestamp is a timeline trim in the video create-audio flow and does
          // not invalidate a block's audio, so exclude it here — otherwise a
          // trim would re-enable the whole-project convert button.
          ...(canCreateBlockAudio
            ? {}
            : { startTime: block?.startTime, endTime: block?.endTime }),
        };
        const originalBlock = {
          text: isTranslationSuccess
            ? originalBlocks[block.id]?.translatedText
            : originalBlocks[block.id]?.text,
          voiceCode: originalBlocks[block.id]?.voiceCode,
          speed: originalBlocks[block.id]?.speed,
          speakerId: originalBlocks[block.id]?.speakerId,
          ...(canCreateBlockAudio
            ? {}
            : {
                startTime: originalBlocks[block.id]?.startTime,
                endTime: originalBlocks[block.id]?.endTime,
              }),
        };
        return (
          isBlockChanged(currentBlock, originalBlock) ||
          !block?.audioLink ||
          audioExpired(block?.audioExpiredAt) ||
          // A failed synthesis leaves the block's text marked as already
          // synthesized (originalBlocks was updated before the request went
          // out), so without this a retry of a block that still holds older
          // audio would look like "nothing to do" and the whole-project convert
          // would skip it.
          block?.status === CONVERTING_STATUS.FAILURE
        );
      }),
    [blockWithText, originalBlocks, isTranslationSuccess, canCreateBlockAudio],
  );

  // Whole-project audio is "ready" (play button shown) when every text block
  // has a valid, non-expired audio (mirrors TTS isAudioLinkExist). The new
  // per-block flow has no merged project audio, so readiness is derived from
  // block statuses instead of audioLinkSrt.
  const isDubbingBlockAudioReady =
    !!blockWithText.length &&
    blockWithText.every(
      (block) =>
        block?.audioLink &&
        block?.status === CONVERTING_STATUS.SUCCESS &&
        !audioExpired(block?.audioExpiredAt),
    );

  // Determinate progress for the convert spinner, computed purely from block
  // statuses (mirrors TTS synthesisProgress): SUCCESS blocks / all text blocks.
  const dubbingBlockSuccessCount = blockWithText.filter(
    (block) =>
      block?.status === CONVERTING_STATUS.SUCCESS &&
      !audioExpired(block?.audioExpiredAt),
  ).length;
  const dubbingSynthesisProgress = blockWithText.length
    ? Math.floor((dubbingBlockSuccessCount * 100) / blockWithText.length)
    : 0;

  // Disable the whole-project convert button when every block that needs audio
  // is already synthesising (mirrors TTS isDisableConvertBtn). Also true when
  // nothing needs audio (0 === 0), so the button is inert until an edit.
  const dubbingBlocksSynthesizingCount = dubbingBlocksNeedingAudio.filter(
    (block) => block?.status === CONVERTING_STATUS.IN_PROGRESS,
  ).length;
  const isDubbingConvertBtnDisabled =
    dubbingBlocksSynthesizingCount === dubbingBlocksNeedingAudio.length;

  // Timeline create-audio button. While one block still lacks audio it reads
  // "Tạo audio" and fills in only those; once they all have audio it becomes
  // "Tạo lại audio" and re-synthesizes everything — which is why the rule above
  // (inert when nothing needs audio) must NOT apply to it, or the button would
  // sit permanently disabled in exactly the state where it has work to do.
  const {
    isReSynthesize: isTimelineReSynthesize,
    targetBlocks: timelineConvertBlocks,
  } = useMemo(
    () =>
      resolveCreateAudioAction({
        blocks: blockWithText,
        blocksNeedingAudio: dubbingBlocksNeedingAudio,
      }),
    [blockWithText, dubbingBlocksNeedingAudio],
  );
  const timelineConvertCredits = useMemo(
    () =>
      calculateBlocksSynthesisCredits(timelineConvertBlocks, {
        speakers,
        voiceDefault,
        useTranslatedText: isTranslationSuccess,
      }),
    [timelineConvertBlocks, speakers, voiceDefault, isTranslationSuccess],
  );

  const isDisabledBtnAudioEffects =
    !blockWithText?.length ||
    hasAudioExpired ||
    isProjectSynthesizing ||
    blockWithTextChanged?.length;

  const isFilteringBlockStatus = useMemo(
    () => currentFilteringStatus !== BLOCK_STATUS.ALL,
    [currentFilteringStatus],
  );

  const isFilteringBlockSpeaker = useMemo(
    () => currentFilteringSpeakerId !== null,
    [currentFilteringSpeakerId],
  );

  const hasSelectedBlock = useMemo(
    () => selectedBlockIds.length > 0,
    [selectedBlockIds],
  );

  const isUsingTurnstile =
    captchaProvider === CAPTCHA_PROVIDER.TURNSTILE &&
    cloudflareTurnstileEnabled;

  const handleCloseOutOfCreditsDialog = () => setOpenOutOfCreditsDialog(false);

  const handleCloseNotificationDialogV2 = () =>
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const renderButtonUnderstand = () => (
    <Button
      variant="contained"
      onClick={handleCloseNotificationDialogV2}
      sx={{
        float: 'right',
        marginLeft: '-10px',
        marginBottom: '-10px',
        padding: '8px 48px',
        borderRadius: '12px',
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 700,
      }}
    >
      {t('gotIt')}
    </Button>
  );

  const renderButtonUnderstandV2 = () => (
    <CommonButton
      variant="outlineSmall"
      onClick={handleCloseNotificationDialogV2}
      customStyle={{
        marginBottom: '0px',
        marginRight: '12px',
        padding: '8px 48px',
        borderRadius: '8px',
        height: '40px',
        textTransform: 'none',
      }}
      textProps={{
        variantText: 'body-14-semibold',
        color: COLOR.darkBlue,
      }}
    >
      {t('gotIt')}
    </CommonButton>
  );

  const handleOpenDiscardedVoiceCloningDialog = () => {
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: {
          name: 'voice-cloning-discard-info',
          title: t('unavailableVoice'),
          description: t('discardedVoiceCloning', {
            style: 'margin-left: -16px',
          }),
          open: true,
          actionComponent: renderButtonUnderstand(),
          showIcon: false,
          onClose: handleCloseNotificationDialogV2,
          variant: null,
        },
      }),
    );
  };

  const handleOpenMaxSpeakersLimitDialog = () => {
    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureAddSpeaker'),
        img: CrownImg,
        classNameImg: 'custom-img',
        maxWidth: '512px',
        showBlurBackground: true,
        onUpgrade: () => {
          history.push(ROUTES.PAYMENT);
          dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
        },
      }),
    );
  };

  const checkValidCreateSynthesis = (blockData) => {
    const validationResult = validateSynthesis({
      blockData,
      onOpenOutOfCreditsDialog: () => setOpenOutOfCreditsDialog(true),
      onOpenExpiredPackageDialog: () => setOpenExpiredPackageDialog(true),
      onOpenDialogLockedCycle: setOpenDialogLockedCycle,
      onOpenMaxCharsLimitExceeded: () => setOpenMaxCharsLimitExceeded(true),
      onOpenLimitedFeatureDialog: openLimitedFeatureDialog,
      onSetWarningOneTimeCredits: setWarningOneTimeCredits,
      onOpenWrongSyntaxDialog: setOpenWrongSyntaxDialog,
      onOpenWrongSyntaxDialogV2: setOpenWrongSyntaxDialogV2,
      onOpenDiscardedVoiceCloning: handleOpenDiscardedVoiceCloningDialog,
      onOpenVoiceTrialLimitDialog: handleOpenVoiceTrialLimitDialog,
      onOpenMaxSpeakersLimitDialog: handleOpenMaxSpeakersLimitDialog,
    });

    return validationResult;
  };

  const renderMaxCharsLimitExceededDialog = () => {
    const isFreePackage = FREE_PACKAGE_CODES.includes(packageCode);

    if (isFreePackage) {
      return (
        <FreeMaxCharsLimitExceeded
          open={openMaxCharsLimitExceeded}
          onClose={handleCloseMaxCharsLimitExceededDialog}
          maxLengthInputText={maxLengthInputText}
        />
      );
    }

    return (
      <PaidMaxCharsLimitExceeded
        open={openMaxCharsLimitExceeded}
        onClose={handleCloseMaxCharsLimitExceededDialog}
        maxLengthInputText={maxLengthInputText}
      />
    );
  };

  const getSidebarWidth = () => {
    let sidebarWidth = 0;
    sidebarWidth = openSidebar ? 0 : SIDEBAR_WIDTH_COLLAPSED;

    return sidebarWidth;
  };
  const handleStopFocusOnBlock = () => {
    setFocusingBlockId(null);
  };

  const handleChangeCharacterExceed = (value) => {
    setCharacterExceed(value);
  };

  const handleFocusingBlock = (blockId) => {
    setFocusingBlockId(blockId);
    dispatch(actions.project.setCurrentBlockId(blockId));
    // In the video timeline flow, focusing a block also seeks playback to the
    // start of that block (no-op for the other engines, which don't expose it).
    if (blockId) seekToBlock?.(blockId);
  };

  // `align` is react-window's: 'center' keeps the long-standing behaviour for
  // error jumps and freshly added blocks, while 'smart' scrolls the minimum
  // needed — and not at all when the block is already on screen.
  //
  // `revealClip` also scrolls the voice track to that block's clip. It is on by
  // default because the callers that jump to a block the user cannot see — an
  // error, a freshly appended block — should move both views. A click on the
  // clip itself must turn it off: the clip is already under the pointer, and
  // moving the track from there is never what the user asked for.
  const scrollToBlock = (
    blockId,
    align = 'center',
    { revealClip = true } = {},
  ) => {
    setScrollToBlockAlign(align);
    setScrollToBlockId(blockId);
    if (revealClip) setRevealClipId(blockId);
    // Clear after a short delay to allow the effect to trigger
    setTimeout(() => {
      setScrollToBlockId(null);
      setRevealClipId(null);
    }, 200);
  };

  // Clicking a clip on the voiceover track focuses its block and brings the text
  // into view.
  //
  // While the PROJECT is playing this does nothing at all: focus belongs to the
  // clock then, which moves it to whatever block the playhead is inside (see
  // syncMedia). Honouring a click would seek the playhead out from under what
  // the user is listening to, steal the caret, and jump the block list — and the
  // next tick would take the highlight straight back anyway.
  //
  // Previewing a single block (`isPlayingBlock`) is NOT that: the playhead isn't
  // running, nothing is claiming focus, and the preview plays on its own audio
  // element — so the tracks stay fully interactive, same as when paused.
  const handleSelectBlockFromTrack = useStableCallback((blockId) => {
    if (!blockId || isPlaying) return;
    // Already dispatches setCurrentBlockId, and seeks playback to the block.
    handleFocusingBlock(blockId);
    scrollToBlock(blockId, 'smart', { revealClip: false });
  });

  const findBlockWithError = (options = {}, blocksToCheck = blockWithText) =>
    blocksToCheck.find((block) => {
      const voice = resolveBlockVoice({ block, speakers, voiceDefault });
      const { hasError } = checkBlockTextError(
        isTranslationSuccess ? block?.translatedText : block?.text,
        maxCharLimitPerBlock,
        blackWordsList,
        voice,
        options,
      );
      return hasError;
    });

  const computeBlockBlackWordsMap = () => {
    const newMap = {};
    blockWithText.forEach((block) => {
      const found = blackWordsList.filter((bw) =>
        containsBlacklistedWords({
          text: isTranslationSuccess ? block?.translatedText : block.text,
          blackWord: bw,
        }),
      );
      if (found.length > 0) newMap[block.id] = found;
    });
    return newMap;
  };

  const getVoiceExist = async () => {
    const voiceExist = await apis.voices.getVoicesV3({
      voiceOwnership: VOICE_OWNERSHIP.VBEE,
      limit: 1,
    });
    if (!voiceExist.status) return [];
    return voiceExist?.result?.voices;
  };
  const getVoiceDefault = async () => {
    const voiceExist = await getVoiceExist();
    if (voiceExist?.length)
      dispatch(actions.project.setVoiceDefault(voiceExist[0]));
  };

  const saveProject = async () => {
    // Under auto-save the project is persisted continuously, so a full save is
    // redundant. Reuse the flush (which no-ops when nothing is pending) so AI
    // features / save points don't show a meaningless loading when everything is
    // already saved. Adapt to the legacy truthy-response contract.
    if (autoSaveEnabled) {
      const saved = await flushAndWaitForSave();
      return saved ? { status: 1 } : null;
    }
    try {
      // TODO: Handle check if there is any block that is error then disable saving project
      const videoSettingsPayload = buildVideoSettingsPayload({
        aspectRatioEnabled: videoDubbingAspectRatioEnabled,
        backgroundColorEnabled: videoDubbingBgColorEnabled,
        videoSettings,
      });
      const response = await updateProject(
        projectId,
        buildProjectPayload({
          title,
          blockIds: blockIdsRef.current,
          blocks: blocksRef.current,
          voiceDefault,
          audioType,
          videoSettings: videoSettingsPayload,
        }),
      );
      return response;
    } catch {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
      return null;
    }
  };

  // Persist the current content before an external action (audio effects,
  // translate, AI generate, leave dialog). Under auto-save, flush the pending
  // auto-save and wait for SAVED; otherwise do the legacy full save. Returns a
  // { status } shape so callers can check success uniformly across both modes.
  const saveProjectBeforeAction = async () => {
    if (!autoSaveEnabled) return saveProject();
    const saved = await flushAndWaitForSave();
    return { status: saved };
  };

  // Ensure pending edits are persisted before a credit-spending synthesis runs.
  // Under auto-save, flush and wait for SAVED; otherwise do a legacy full save.
  // Returns true when it is safe to proceed.
  const ensureSavedBeforeSynthesis = async () => {
    if (autoSaveEnabled) return flushAndWaitForSave();
    const response = await saveProject();
    return Boolean(response?.status);
  };

  useEffect(() => {
    blockIdsRef.current = blockIds;
  }, [blockIds]);

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    currentFilteringStatusRef.current = currentFilteringStatus;
  }, [currentFilteringStatus]);

  useEffect(() => {
    currentFilteringSpeakerIdRef.current = currentFilteringSpeakerId;
  }, [currentFilteringSpeakerId]);

  useEffect(() => {
    synthesisRequestRef.current = synthesisRequest;
  }, [synthesisRequest]);

  useEffect(() => {
    getVoiceDefault();
  }, []);

  useEffect(() => {
    // Under auto-save the unload warning is handled by useUnloadGuard.
    if (autoSaveEnabled) return undefined;

    const handleBeforeUnload = async (event) => {
      if (isDraft || isProjectSynthesizing) {
        if (!isProjectSynthesizing) {
          saveProject();
        }
        event.preventDefault();
        const e = event;
        e.returnValue = '';
        return undefined;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    autoSaveEnabled,
    isDraft,
    t,
    isOpenUnsavedChangesDialog,
    blockIds,
    blocks,
  ]);

  useEffect(() => {
    if (!isBlockUIImprovement) return undefined;
    const fetchBlackWords = async () => {
      const data = await apis.blackWords.getBlackWords();
      if (data?.status) {
        setBlackWordsList(
          data.result.blackWords.map((bw) => bw.word?.toLowerCase()),
        );
      }
    };
    fetchBlackWords();
    return undefined;
  }, [isBlockUIImprovement]);

  const onLeavePage = () => {
    setIsOpenUnsavedChangesDialog(false);
    dispatch(actions.project.updateOriginalBlocks());
  };

  const handleCloseUnsavedChangesDialog = () => {
    onLeavePage();
  };

  const generateVoiceSummary = (voices) => {
    if (!Array.isArray(voices) || voices.length === 0) {
      return "<ul style='margin: 0 0 0 10px; padding: 0;'>\n<li>No voices available.</li>\n</ul>";
    }

    const countCredits = blockWithText.reduce((sum, block) => {
      const blockText = isTranslationSuccess
        ? block?.translatedText || ''
        : block.text || '';
      const textLength = countTextLength(blockText);
      const voice =
        block.voice ||
        speakers?.find((speaker) => speaker.voiceCode === block.voiceCode)
          ?.voice;
      const factor = voice?.creditFactor || 1;
      return sum + textLength * factor;
    }, 0);

    const voiceList = voices
      .map((item) => {
        const voiceName = item.name;
        const voiceLevel = item.level ? t(item.level) : '';
        const isBeta = item.beta ? 'Beta' : '';
        // voiceNote combine voiceLevel and isBeta
        const voiceNote = [voiceLevel, isBeta].filter(Boolean).join(' - ');
        const factor = item.creditFactor || 1;
        return t('voiceFactor', {
          voiceName,
          voiceNote: voiceNote ? `(${voiceNote})` : '',
          isBeta,
          factor,
        });
      })
      .join('\n');

    return t('totalFactorCredits', {
      voiceList,
      countCredits: countCredits?.toLocaleString(language),
    });
  };

  const showWarningFactorCharcterDialog = () => {
    const highCreditFactorVoices = blockWithText
      .map((block) => {
        const voice =
          block.voice ||
          speakers?.find((speaker) => speaker.voiceCode === block.voiceCode)
            ?.voice;
        return voice;
      })
      .filter((voice) => voice?.creditFactor > 1);

    const highCreditFactorVoicesUnique = highCreditFactorVoices.filter(
      (currVoice, index, self) =>
        index ===
        self.findIndex(
          (tVoice) =>
            tVoice.code === currVoice.code &&
            tVoice.provider === currVoice.provider,
        ),
    );

    if (!highCreditFactorVoicesUnique.length) return false;
    setOpenWrongSyntaxDialogV2({
      name: 'error-syntax',
      variant: 'warning',
      title: t('note'),
      description: generateVoiceSummary(highCreditFactorVoicesUnique),
      type: DIALOG_V2_TYPE.NON_VBEE_WARNING,
    });
    return true;
  };

  const calculateVoiceCreditsMapWithTimes = (blocksList) => {
    const voiceCreditsMap = calculateVoiceCreditsMap(blocksList);

    // Count sentences per voice using reduce
    const voiceTimesMap = blocksList.reduce((acc, block) => {
      const code = block?.voice?.code;
      if (code) {
        acc[code] = 1;
      }
      return acc;
    }, {});

    // Add times to each voice and convert to object with voice code as key
    return Object.values(voiceCreditsMap).reduce((acc, item) => {
      acc[item.voice?.code] = {
        ...item,
        times: voiceTimesMap[item.voice?.code] || 1,
      };
      return acc;
    }, {});
  };

  const getTrialUsageCandidates = () => {
    if (!enableVoiceTrial) return [];

    const voiceCreditsMapWithTimes =
      calculateVoiceCreditsMapWithTimes(blockWithText);
    return Object.values(voiceCreditsMapWithTimes);
  };

  const getInactiveVoices = () => {
    // 1. Get all unique voice codes
    const voiceCodes = [
      ...new Set(blockWithText.map((block) => block.voiceCode)),
    ];
    // 2. For each voice code, get the voice info from the speaker
    const voiceInfos = voiceCodes.map((voiceCode) => {
      const voice = speakers?.find(
        (speaker) => speaker.voiceCode === voiceCode,
      ).voice;
      return voice;
    });

    // 3. Filter the voice that has active === false
    const inactiveVoices = voiceInfos.filter(
      (voice) => voice?.active === false,
    );
    return inactiveVoices;
  };

  const getDisplayInactiveVoicesText = (inactiveVoices) => {
    const voiceCodes = inactiveVoices.map((voice) => voice?.code);
    const blocksWithVoices = Object.values(blocks).filter(
      (block) => block?.voiceCode && voiceCodes.includes(block.voiceCode),
    );

    // Build voice list with speaker information
    const voiceNamesList = inactiveVoices
      .map((voice) => {
        // Find blocks using this voice
        const blocksUsingVoice = blocksWithVoices.filter(
          (block) => block.voiceCode === voice?.code,
        );

        // Get unique speakers for this voice
        const speakerIds = [
          ...new Set(
            blocksUsingVoice.map((block) => block?.speakerId).filter(Boolean),
          ),
        ];

        // Get speaker names
        const speakerNames = speakerIds
          .map((speakerId) => {
            const speaker = speakers?.find((s) => s.id === speakerId);
            return speaker?.name || '';
          })
          .filter(Boolean);

        const voiceName = voice?.name || '';
        const sanitizedVoiceName = sanitizeHtml(voiceName);

        const formatVoiceText = (withSpeaker) => {
          const voiceNameFormatted = `<b>${sanitizedVoiceName}</b>`;

          if (!withSpeaker) {
            return `<li>${voiceNameFormatted}</li>`;
          }

          const speakerLabel = t('speaker');
          const speakerText = sanitizeHtml(speakerNames.join(', '));
          const speakerInfo = `${speakerLabel}: <i>${speakerText}</i>`;
          const fullText = `${voiceNameFormatted} (${speakerInfo})`;

          return `<li>${fullText}</li>`;
        };

        return formatVoiceText(speakerNames.length > 0);
      })
      .filter(Boolean);

    return voiceNamesList;
  };

  const handleErrorInactiveVoice = () => {
    const inactiveVoices = getInactiveVoices();
    const displayInactiveVoicesText =
      getDisplayInactiveVoicesText(inactiveVoices);
    dispatch(
      actions.dialog.displayDialogV2({
        notificationDialogV2: {
          name: 'voice-cloning-discard-info',
          title: t('unavailableVoiceV2'),
          description: t('inactiveVoiceNotiV2', {
            voiceNames: displayInactiveVoicesText,
          }),
          open: true,
          actionComponent: renderButtonUnderstandV2(),
          showIcon: false,
          onClose: handleCloseNotificationDialogV2,
          variant: null,
          maxWidth: '640px',
          padding: '12px 24px',
          paddingHeader: '8px 24px',
          widthContent: '640px',
        },
      }),
    );
  };

  const handleConvertAudio = async () => {
    // Reset synthesis request to avoid keeping old values
    dispatch(
      actions.synthesisRequest.updateSynthesisConfig({
        id: null,
        processingAt: null,
        endedAt: null,
        progress: 0,
        status: null,
        characters: 0,
      }),
    );

    dispatch(actions.project.setIsProjectConvertingDraft(true));
    dispatch(actions.project.setIsProjectSynthesizing(true));
    dispatch(actions.project.setAudioLinkSrt(null));
    dispatch(actions.project.setAudioSrtExpiredAt(null));

    const trialUsageCandidates = getTrialUsageCandidates();
    const options = {};
    if (isUsingTurnstile) {
      options.turnstileToken = await turnstileWidgetRef.current.onVerify();
      turnstileWidgetRef.current.reset();
    }

    // Call API to synthesize dubbing project
    const data = await apis.project.synthesisProject(projectId, options);

    // Update synthesis data
    const isCreatedReqSuccess = data?.status;
    if (isCreatedReqSuccess) {
      dispatch(actions.project.deleteAllBlocksCredits());
      // One request covers every speaker, so each distinct voice counts once
      const submittedVoices = new Map();
      blockWithText.forEach((block) => {
        const voice = resolveBlockVoice({ block, speakers, voiceDefault });
        if (voice?.code) submittedVoices.set(voice.code, voice);
      });
      submittedVoices.forEach((voice) =>
        trackTryVoice({ voice, action: TRY_VOICE_ACTION.CONVERT }),
      );

      // Fetch project to get requestId, then fetch request details
      const responseProject = await apis.project.getProject(
        projectId,
        ['requestId'].join(','),
      );

      if (responseProject?.status) {
        const { project } = responseProject.result;
        // First block's voice stands in for the whole project (mostly single-voice)
        registerRequestMeta(project?.requestId, {
          module: MOE_MODULE.PROJECT,
          voice: blockWithText[0]?.voice || voiceDefault,
        });
        const requestDetail = await apis.requests.getRequestV2(
          project.requestId,
        );
        dispatch(actions.project.setDubbingRequestId(project?.requestId));
        dispatch(actions.project.setIsProjectConvertingDraft(false));
        if (requestDetail?.status) {
          dispatch(
            actions.synthesisRequest.updateSynthesisConfig(
              requestDetail.result,
            ),
          );
        }
      }

      const updatedTrialUsage = buildUpdatedTrialUsage({
        currentUsage: stateTrialVoiceUsage,
        voices: trialUsageCandidates,
        config: voiceTrialConfig,
        userFeatures: ttsUser?.features,
      });
      if (updatedTrialUsage) {
        dispatch(actions.user.updateTrialVoiceUsage(updatedTrialUsage));
      }

      const totalUsedCredits = blockWithText.reduce((acc, block) => {
        const blockText = isTranslationSuccess
          ? block?.translatedText || ''
          : block.text || '';
        return acc + countTextLength(blockText);
      }, 0);
      const isNotifyLowCredits = checkShouldNotifyLowCredits({
        beforeCredits: totalCredits,
        afterCredits: totalCredits - totalUsedCredits,
        packageCode: user.packageCode,
      });
      if (isNotifyLowCredits) setNotifyLowCreditsDialog(true);
    }

    if (!isCreatedReqSuccess) {
      dispatch(
        actions.synthesisRequest.updateSynthesisConfig({
          id: null,
          processingAt: null,
          endedAt: null,
          progress: 0,
          status: null,
          characters: 0,
        }),
      );
      dispatch(actions.project.setIsProjectConvertingDraft(false));
      dispatch(actions.project.setIsProjectSynthesizing(false));
      const { errorMessage, errorCode } = data;
      switch (errorCode) {
        case ERROR_CODE.INVALID_SYNTAX: {
          setOpenWrongSyntaxDialog({
            name: 'error-syntax',
            variant: 'warning',
            title: t('errorSyntax'),
            description: t('errorSyntaxDescription'),
          });
          break;
        }

        case ERROR_CODE.SPAM_ACCOUNT:
        case ERROR_CODE.EXCEED_RATE_LIMIT: {
          setOpenWrongSyntaxDialogV2({
            name: 'error-syntax',
            variant: 'warning',
            title: t('cannotConvertTTS'),
            description: t('blockSpamEmailDesc'),
            type: DIALOG_V2_TYPE.BLOCK_SYNTHESIS,
          });
          break;
        }

        case ERROR_CODE.EXCEED_MAX_TOTAL_CHARACTERS: {
          trackPaywall(MOE_PAYWALL_TYPE.MAX_CHARS, {
            trigger: MOE_PAYWALL_TRIGGER.SERVER_ERROR,
          });
          setOpenMaxCharsLimitExceeded(true);
          break;
        }

        case ERROR_CODE.TEXT_TOO_LONG: {
          trackPaywall(MOE_PAYWALL_TYPE.OUT_OF_CREDITS, {
            trigger: MOE_PAYWALL_TRIGGER.SERVER_ERROR,
          });
          setOpenOutOfCreditsDialog(true);
          break;
        }

        case ERROR_CODE.LIMIT_FREE_INSTANT_VOICE_SYNTHESIS: {
          // TODO: Pass max instant synthesis to dialog
          dispatch(
            actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
              description: t('unlockUnlimitedInstantVoiceSynthesis'),
              img: CrownImg,
              classNameImg: 'custom-img',
              maxWidth: 'sm',
            }),
          );
          break;
        }

        case ERROR_CODE.INACTIVE_VOICE: {
          handleErrorInactiveVoice();
          break;
        }

        case ERROR_CODE.NO_MORE_SUPPORT_VOICE: {
          const userEOLDate = user?.studio?.oneTime?.eolDate;
          const voiceNames = blockWithText
            ?.filter((block) => {
              const { voiceCode } = block;
              const voiceInfo = speakers?.find(
                (speaker) => speaker.voiceCode === voiceCode,
              ).voice;
              const voiceEOLDate = voiceInfo?.eolDate;
              if (!voiceEOLDate || !userEOLDate) return true;
              const latestEOLDate = new Date(
                Math.max(
                  new Date(voiceEOLDate).getTime(),
                  new Date(userEOLDate).getTime(),
                ),
              );
              return moment().isAfter(latestEOLDate);
            })
            .map((block) => block.voice?.name)
            .filter(Boolean)
            .join(', ');

          dispatch(
            actions.noti.push({
              severity: 'error',
              message: t('inactiveVoiceNoti', {
                voiceName: voiceNames,
              }),
              value: errorMessage,
            }),
          );
          break;
        }

        case ERROR_CODE.DISCARDED_VOICE_CLONING: {
          handleOpenDiscardedVoiceCloningDialog();
          break;
        }

        case ERROR_CODE.SPEAKER_LIMIT_EXCEEDED: {
          handleOpenMaxSpeakersLimitDialog();
          break;
        }

        case ERROR_CODE.VOICE_TRIAL_LIMIT_REACHED: {
          const response = await apis.user.getTrialVoiceUsage();
          const latestTrialUsage =
            response?.result?.voiceUsage || stateTrialVoiceUsage;
          dispatch(actions.user.updateTrialVoiceUsage(latestTrialUsage));
          const voiceCreditsMapWithTimes =
            calculateVoiceCreditsMapWithTimes(blockWithText);

          // Use latest trial usage to find exceeded voices
          const exceededVoices = findExceededTrialVoices(
            voiceCreditsMapWithTimes,
            voiceTrialConfig,
            latestTrialUsage,
            ttsUser?.features,
            checkFeaturePermission,
          );

          if (exceededVoices.length > 0) {
            const exceededVoiceObjects = exceededVoices.map(
              (item) => item.voice,
            );
            handleOpenVoiceTrialLimitDialog(exceededVoiceObjects);
          } else {
            dispatch(
              actions.noti.push({
                severity: 'error',
                message:
                  errorCode === ERROR_CODE.BAD_REQUEST
                    ? 'errorOccurred'
                    : 'synthesisFailure',
                value: errorMessage,
              }),
            );
          }
          break;
        }

        default: {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message:
                errorCode === ERROR_CODE.BAD_REQUEST
                  ? 'errorOccurred'
                  : 'synthesisFailure',
              value: errorMessage,
            }),
          );
        }
      }
    }
  };

  // dubbing per-block synthesis enforces the same voice-trial limits.
  const getTrialUsageCandidatesForBlocks = (blocksList = []) => {
    if (!enableVoiceTrial || !Array.isArray(blocksList)) return [];

    const voiceCreditsMap = {};

    blocksList.forEach((blockItem) => {
      if (!blockItem?.id) return;
      const voice = blocksRef.current?.[blockItem.id]?.voice;
      if (!voice?.code) return;

      const isTrialVoice = isUsingTrialVoice(
        voice,
        voiceTrialConfig,
        ttsUser?.features,
        checkFeaturePermission,
      );
      if (!isTrialVoice) return;

      const text =
        blockItem?.text ?? blocksRef.current?.[blockItem.id]?.text ?? '';
      const characters = countTextLength(text || '');
      const creditFactor = voice?.creditFactor || 1;
      const credits = characters * creditFactor;

      if (!voiceCreditsMap[voice.code]) {
        voiceCreditsMap[voice.code] = { voice, credits: 0, times: 0 };
      }
      voiceCreditsMap[voice.code].credits += credits;
      voiceCreditsMap[voice.code].times += 1;
    });

    return Object.values(voiceCreditsMap);
  };

  const findExceededTrialVoicesForBlocks = (
    blocksList = [],
    usage = stateTrialVoiceUsage,
  ) => {
    if (!enableVoiceTrial) return [];
    const candidates = getTrialUsageCandidatesForBlocks(blocksList);
    if (!candidates.length) return [];
    return candidates.filter(({ voice, credits, times }) =>
      willExceedTrialLimit(voice, voiceTrialConfig, usage, credits, times),
    );
  };

  const updateTrialUsageAfterSuccess = (blocksList = [], requestIds = []) => {
    if (!enableVoiceTrial) return;
    const candidates = getTrialUsageCandidatesForBlocks(blocksList);
    if (!candidates.length) return;

    if (requestIds.length) {
      requestIds.forEach((requestId) => {
        if (requestId) {
          dispatch(
            actions.synthesisRequest.setSynthesizingVoice(
              requestId,
              candidates,
            ),
          );
        }
      });
    }

    const updatedTrialUsage = buildUpdatedTrialUsage({
      currentUsage: stateTrialVoiceUsage,
      voices: candidates,
      config: voiceTrialConfig,
      userFeatures: ttsUser?.features,
    });
    if (updatedTrialUsage) {
      dispatch(actions.user.updateTrialVoiceUsage(updatedTrialUsage));
    }
  };

  const handleUpdateCredits = (idBlock, text, creditFactor) => {
    const credits = countTextLength(text) * (creditFactor || 1);
    dispatch(actions.project.updateCreditsBlocks(idBlock, credits));
  };

  // blockId → the requestId the create call handed back for the run in flight.
  // The status poll needs it to recognise its own result: the Backend can answer
  // with an existing request (same text/voice/speed), and then the audio link it
  // reports is the one the block already has, so nothing in the response marks
  // the synthesis as finished. Cleared the moment a block is marked IN_PROGRESS
  // so a poll that lands before the create response can't match the previous
  // run's id and clear the spinner early. A ref, not state: only the poll reads
  // it, and it must never trigger a render of the block list.
  const submittedRequestIdsRef = useRef(new Map());

  const markBlocksSynthesizing = (ids = []) =>
    ids.forEach((id) => {
      if (id) submittedRequestIdsRef.current.delete(id);
    });

  // Take blocks out of the synthesizing state after a failed run, and drop the
  // credit preview that was added when they entered it. Every convert entry
  // point skips IN_PROGRESS blocks, so a block that is not settled here keeps
  // its spinner forever and is silently passed over by the next convert — which
  // is why every failure path routes through this one function.
  const settleBlocksSynthesisFailure = (ids = [], { notify = false } = {}) => {
    const updates = buildBlockSynthesisFailureUpdates(ids);
    if (!Object.keys(updates).length) return;
    // One batched dispatch, whatever the batch size: on React 17 a per-block
    // dispatch from this async path is a separate render of the whole list.
    dispatch(actions.project.updateMultiBlockSynthesisStatus(updates));
    if (notify) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'synthesisFailure',
        }),
      );
    }
  };

  // handleConvertBlockToAudio (payload carries NO timestamp). The block audio
  // result arrives via handleFetchDubbingBlockSynthesisInfo polling below.
  const runBlockSynthesisRequest = async (block) => {
    // After a successful translation the block is synthesized from its
    // translatedText, not the original text (mirrors the selector used across
    // this file for filtering/char-count/change-detection).
    const synthesisText = isTranslationSuccess
      ? block?.translatedText
      : block?.text;
    // Add credit preview + show the in-progress spinner immediately (mirrors
    // TTS handleAddBlockToQueue before it enqueues the block).
    handleUpdateCredits(block.id, synthesisText, block?.voice?.creditFactor);
    markBlocksSynthesizing([block.id]);
    dispatch(
      actions.project.updateBlockSynthesisInfo(block.id, {
        status: CONVERTING_STATUS.IN_PROGRESS,
      }),
    );

    // Persist pending edits before synthesis, and WAIT for them (mirrors TTS
    // handleAddBlockToQueue). Auto-save is debounced, so at this point an edit
    // may still be un-saved or its request in flight: synthesizing then reads
    // the stale text from the DB, and the save that lands afterwards overwrites
    // the block — wiping the audioLink the synthesis just produced. Under
    // auto-save this flushes and waits for SAVED; otherwise it does a full save.
    const saved = await ensureSavedBeforeSynthesis();
    if (!saved) {
      settleBlocksSynthesisFailure([block.id]);
      // Nothing was submitted, so the credit preview added above has to go —
      // otherwise it keeps inflating the displayed cost.
      dispatch(actions.project.deleteCreditsBlocks(block.id));
      return;
    }

    dispatch(actions.project.updateOriginalBlocks(block.id));

    const blockSpeaker = speakers?.find(
      (speaker) => speaker.id === block?.speakerId,
    );

    const synthesisPayload = {
      text: synthesisText,
      voiceCode: blockSpeaker?.voiceCode || voiceDefault?.code,
      speed: block?.speed || DEFAULT_SPEED,
      projectId,
      blockId: block.id,
      audioType,
      ...(block?.hasEmphasis ? { emphasisIntensity: block?.emphasis } : {}),
    };

    const options = {};
    if (isUsingTurnstile) {
      options.turnstileToken = await turnstileWidgetRef.current.onVerify();
      turnstileWidgetRef.current.reset();
    }

    const data = await apis.synthesis.createSynthesis(
      synthesisPayload,
      options,
    );
    if (!blockIdsRef.current.includes(block.id)) return;

    const isCreatedReqSuccess = data?.status;
    if (isCreatedReqSuccess && data.result?.requestId) {
      submittedRequestIdsRef.current.set(block.id, data.result.requestId);
      registerRequestMeta(data.result.requestId, {
        module: MOE_MODULE.PROJECT,
        voice: blockSpeaker?.voice || voiceDefault,
      });
      trackTryVoice({
        voice: pickSubmittedVoice(synthesisPayload.voiceCode, [
          blockSpeaker?.voice,
          voiceDefault,
        ]),
        action: TRY_VOICE_ACTION.CONVERT,
      });
    }
    dispatch(
      actions.project.updateBlockSynthesisInfo(block.id, {
        ...(isCreatedReqSuccess
          ? { requestId: data.result?.requestId }
          : { status: CONVERTING_STATUS.FAILURE }),
      }),
    );
    dispatch(actions.project.deleteCreditsBlocks(block.id));

    dispatch(
      actions.project.updateBlockStatus({
        blockId: block.id,
        blockStatus: isCreatedReqSuccess
          ? BLOCK_STATUS.IN_PROGRESS
          : BLOCK_STATUS.ERROR,
      }),
    );

    if (isCreatedReqSuccess) {
      const requestId = data.result?.requestId;
      updateTrialUsageAfterSuccess([block], requestId ? [requestId] : []);
      const isNotifyLowCredits = checkShouldNotifyLowCredits({
        beforeCredits: totalCredits,
        afterCredits: totalCredits - (countTextLength(synthesisText) || 0),
        packageCode: user.packageCode,
      });
      if (isNotifyLowCredits) setNotifyLowCreditsDialog(true);
      return;
    }

    const { errorMessage, errorCode } = data;
    // Multi-block aggregation of trial-limit failures belongs to the queue flow
    // (PR3); for the single-block trigger fall through to per-error dialogs.
    if (block?.synthesisContext === SYNTHESIS_CONTEXT.MULTI_BLOCK) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message:
            errorCode === ERROR_CODE.BAD_REQUEST
              ? 'errorOccurred'
              : 'synthesisFailure',
          value: errorMessage,
        }),
      );
      return;
    }

    switch (errorCode) {
      case ERROR_CODE.INVALID_SYNTAX: {
        setOpenWrongSyntaxDialog({
          name: 'error-syntax',
          variant: 'warning',
          title: t('errorSyntax'),
          description: t('errorSyntaxDescription'),
        });
        break;
      }

      case ERROR_CODE.SPAM_ACCOUNT:
      case ERROR_CODE.EXCEED_RATE_LIMIT: {
        setOpenWrongSyntaxDialogV2({
          name: 'error-syntax',
          variant: 'warning',
          title: t('cannotConvertTTS'),
          description: t('blockSpamEmailDesc'),
          type: DIALOG_V2_TYPE.BLOCK_SYNTHESIS,
        });
        break;
      }

      case ERROR_CODE.TEXT_TOO_LONG: {
        trackPaywall(MOE_PAYWALL_TYPE.OUT_OF_CREDITS, {
          trigger: MOE_PAYWALL_TRIGGER.SERVER_ERROR,
        });
        setOpenOutOfCreditsDialog(true);
        break;
      }

      case ERROR_CODE.LIMIT_FREE_INSTANT_VOICE_SYNTHESIS: {
        dispatch(
          actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
            description: t('unlockUnlimitedInstantVoiceSynthesis'),
            img: CrownImg,
            classNameImg: 'custom-img',
            maxWidth: 'sm',
          }),
        );
        break;
      }

      case ERROR_CODE.INACTIVE_VOICE:
      case ERROR_CODE.NO_MORE_SUPPORT_VOICE: {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: t('inactiveVoiceNoti', { voiceName: block?.voice?.name }),
            value: errorMessage,
          }),
        );
        break;
      }

      case ERROR_CODE.DISCARDED_VOICE_CLONING: {
        handleOpenDiscardedVoiceCloningDialog();
        break;
      }

      case ERROR_CODE.VOICE_TRIAL_LIMIT_REACHED: {
        const responseTrial = await apis.user.getTrialVoiceUsage();
        const latestTrialUsage =
          responseTrial?.result?.voiceUsage || stateTrialVoiceUsage;
        dispatch(actions.user.updateTrialVoiceUsage(latestTrialUsage));
        const exceededVoices = findExceededTrialVoicesForBlocks(
          [block],
          latestTrialUsage,
        );
        if (exceededVoices.length) {
          handleOpenVoiceTrialLimitDialog(
            exceededVoices.map((item) => item.voice),
          );
        } else {
          dispatch(
            actions.noti.push({
              severity: 'error',
              message:
                errorCode === ERROR_CODE.BAD_REQUEST
                  ? 'errorOccurred'
                  : 'synthesisFailure',
              value: errorMessage,
            }),
          );
        }
        break;
      }

      default: {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message:
              errorCode === ERROR_CODE.BAD_REQUEST
                ? 'errorOccurred'
                : 'synthesisFailure',
            value: errorMessage,
          }),
        );
      }
    }
  };

  // The block is marked IN_PROGRESS before the request goes out, so anything
  // that throws on the way to a status — the turnstile challenge, a rejected
  // fetch, the trial-usage lookup in the error branch — would otherwise leave it
  // synthesizing forever: the next convert skips IN_PROGRESS blocks, so the
  // spinner never stops and nothing ever retries it. Settle it here instead, and
  // let the caller keep going with the rest of its batch.
  const handleConvertBlockToAudioDubbing = async (block) => {
    try {
      await runBlockSynthesisRequest(block);
    } catch {
      settleBlocksSynthesisFailure([block?.id], { notify: true });
      dispatch(actions.project.deleteCreditsBlocks(block?.id));
    }
  };

  // The per-block Convert button. The multi-select and whole-project flows both
  // check the text before synthesizing; this one ran nothing — and
  // checkValidCreateSynthesis, which NewSynthesisButton does call, covers
  // credits / package / limits / syntax but knows nothing about the black-word
  // list. A block holding a banned word therefore synthesized straight through.
  //
  // Deliberately a copy of TTS handleAddBlockToQueue, which is the flow that
  // behaves correctly: check THIS block only, for black words only, and merge
  // just its entry into the map — the block turns red naming the word, and no
  // other block is marked by a button the user pressed on this one. No scroll
  // either: the block is already under the pointer. The wider findBlockWithError
  // belongs to the flows that convert blocks the user may not be looking at.
  //
  // Voice and text resolve exactly as in findBlockWithError (own speaker's
  // voice, translated text once translation succeeded), so this agrees with what
  // the whole-project button would say about the same block.
  //
  // Wrapped around handleConvertBlockToAudioDubbing rather than folded into it:
  // handleConvertBlockSelected drives that in a loop over blocks it has ALREADY
  // validated.
  const handleConvertSingleBlockDubbing = async (block) => {
    if (!block) return;
    const { hasBlackWords, blackWordsFound } = checkBlockTextError(
      isTranslationSuccess ? block?.translatedText : block?.text,
      maxCharLimitPerBlock,
      blackWordsList,
      resolveBlockVoice({ block, speakers, voiceDefault }),
    );
    if (hasBlackWords) {
      setBlockBlackWordsMap((prev) => ({
        ...prev,
        [block.id]: blackWordsFound,
      }));
      return;
    }
    await handleConvertBlockToAudioDubbing(block);
  };

  // Create audio for the selected blocks (mirrors TTS
  // handleConvertBlockSelected). Reuses the single-block flow sequentially so
  // requests are paced; re-synthesises every selected block, incl. ones that
  // already have audio.
  const handleConvertBlockSelected = async (blocksSelected) => {
    const blocksToConvert = pickConvertibleBlocks(blocksSelected, {
      useTranslatedText: isTranslationSuccess,
    });
    if (!blocksToConvert.length) return;
    // Mirror TTS handleConvertBlockSelected: client-side per-block check first
    // (scrolls to the offending block), then shared synthesis validation — an
    // invalid block must never reach the synthesis API.
    const errorBlock = findBlockWithError({}, blocksToConvert);
    if (errorBlock) {
      scrollToBlock(errorBlock.id);
      return;
    }
    if (!checkValidCreateSynthesis(blocksToConvert)) return;
    // Mark every selected block in-progress up front (single batched dispatch)
    // so all spinners show and the toolbar convert button disables immediately
    // (mirrors TTS), instead of flipping block-by-block as requests complete.
    const inProgressUpdates = blocksToConvert.reduce((acc, block) => {
      acc[block.id] = {
        status: CONVERTING_STATUS.IN_PROGRESS,
        blockStatus: BLOCK_STATUS.IN_PROGRESS,
      };
      return acc;
    }, {});
    markBlocksSynthesizing(blocksToConvert.map((block) => block.id));
    dispatch(
      actions.project.updateMultiBlockSynthesisStatus(inProgressUpdates),
    );
    // Wait for the pending auto-save BEFORE the loop, for the whole batch: the
    // per-block flush below would only roll the FIRST block back on failure and
    // leave the rest stuck IN_PROGRESS. Once this resolves the flush inside
    // handleConvertBlockToAudioDubbing is a no-op (status is already SAVED).
    const saved = await ensureSavedBeforeSynthesis();
    if (!saved) {
      settleBlocksSynthesisFailure(blocksToConvert.map((block) => block.id));
      return;
    }
    // Fire synthesis sequentially to pace requests (like the TTS queue). One
    // block failing must never abort the batch: the blocks after it were already
    // marked IN_PROGRESS above, so an escaping throw would strand every one of
    // them on a spinner that nothing can clear.
    for (let i = 0; i < blocksToConvert.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await handleConvertBlockToAudioDubbing(blocksToConvert[i]);
      } catch {
        settleBlocksSynthesisFailure([blocksToConvert[i]?.id], {
          notify: true,
        });
      }
    }
  };

  // A synthesis waiting on the user's confirmation:
  // `{ blocks, isWholeProject }`, or null when no dialog is up. Both entry
  // points that spend credits on existing audio — "Tạo lại audio" and
  // converting a hand-picked selection — park their work here and run it from
  // handleConfirmConvert, so there is one dialog and one place that starts them.
  const [confirmConvert, setConfirmConvert] = useState(null);

  const confirmConvertCredits = useMemo(
    () =>
      calculateBlocksSynthesisCredits(confirmConvert?.blocks, {
        speakers,
        voiceDefault,
        useTranslatedText: isTranslationSuccess,
      }),
    [confirmConvert, speakers, voiceDefault, isTranslationSuccess],
  );

  const handleCloseConfirmConvert = useStableCallback(() =>
    setConfirmConvert(null),
  );

  const handleConfirmConvert = useStableCallback(async () => {
    const pending = confirmConvert;
    setConfirmConvert(null);
    if (!pending?.blocks?.length) return;
    // Re-read every block from the store before converting. `pending.blocks`
    // holds the objects as they were when the dialog opened, and UPDATE_BLOCK
    // replaces objects rather than mutating them — so a rewrite that started
    // while the dialog was up (the progress poll writes PENDING from the server)
    // is invisible to the snapshot, and pickConvertibleBlocks downstream would
    // let it through. A block deleted meanwhile is dropped, not resurrected.
    const currentBlocks = blocksRef.current || {};
    const blocksToConvert = pending.blocks
      .map((block) => currentBlocks[block.id])
      .filter(Boolean);
    if (!blocksToConvert.length) return;
    if (pending.isWholeProject) setIsConvertingAllBlocks(true);
    await handleConvertBlockSelected(blocksToConvert);
  });

  // What the multi-select toolbar calls instead of converting directly — the
  // selection may well already have audio, so it goes through the dialog. The
  // toolbar hands over its raw selection; narrowing it happens here, once, so
  // the dialog prices exactly what the confirm will submit.
  const handleRequestConvertBlockSelected = useStableCallback(
    (blocksSelected) => {
      const blocksToConvert = pickConvertibleBlocks(blocksSelected, {
        useTranslatedText: isTranslationSuccess,
      });
      if (!blocksToConvert.length) return;
      setConfirmConvert({ blocks: blocksToConvert, isWholeProject: false });
    },
  );

  // Whole-project convert for the new per-block flow (mirrors TTS
  // handleConvertAudio in TTS/Content). Runs shared pre-checks once, then
  // synthesises every block that still needs audio (changed, missing, or
  // expired) via the same per-block flow used by the toolbar. Only wired in
  // when `canCreateBlockAudio`; otherwise the legacy single-request
  // `preProcessSynthesizeDubbing` flow is used instead.
  const handleConvertAllBlocksDubbing = async () => {
    const newBlackWordsMap = computeBlockBlackWordsMap();
    setBlockBlackWordsMap(newBlackWordsMap);
    const errorBlock = findBlockWithError();
    if (errorBlock) {
      scrollToBlock(errorBlock.id);
      return;
    }
    dispatch(actions.project.setIsPlaying(false));
    dispatch(actions.project.setIsMultiBlockMode(false));
    dispatch(actions.project.setCurrentBlockId(null));
    dispatch(actions.project.setIsPlayingBlock(false));
    if (!checkValidCreateSynthesis()) return;

    // Blocks that still need audio, or — when they all already have it — every
    // block, because that is the "Tạo lại audio" case. Either way the list is
    // the same one the tooltip and the confirmation priced.
    const blocksToConvert = timelineConvertBlocks;
    if (!blocksToConvert.length) return;

    // Re-creating audio spends credits on audio the user already owns, so ask
    // first; filling in missing audio does not and runs straight away.
    if (isTimelineReSynthesize) {
      setConfirmConvert({ blocks: blocksToConvert, isWholeProject: true });
      return;
    }

    // Mark this as a whole-project convert so the AudioPlayer shows % progress
    // (mirrors TTS). Cleared when synthesis settles (effect below).
    setIsConvertingAllBlocks(true);
    await handleConvertBlockSelected(blocksToConvert);
  };

  // After block(s) receive fresh audio, realign each block's endTime to the
  // audio length shrink to fit a
  // shorter audio. Local store update keeps the timeline + per-block
  // playback duration correct; autoSaveTrigger persists it. Video create-audio
  // flow only (guarded by canCreateBlockAudio).
  const adjustBlocksEndTimeToAudio = async (blocksGotAudio) => {
    if (!canCreateBlockAudio || !blocksGotAudio?.length) return;
    const currentBlocks = blocksRef.current || {};
    const durations = await Promise.all(
      blocksGotAudio.map((b) => loadAudioDurationFromUrl(b.audioLink)),
    );
    const endTimeUpdates = blocksGotAudio.reduce((acc, b, i) => {
      const storeBlock = currentBlocks[b.id];
      if (!storeBlock) return acc;
      const newEndTime = getAudioAdjustedBlockEndTime({
        startTime: storeBlock.startTime,
        endTime: storeBlock.endTime,
        audioDuration: durations[i],
      });
      if (newEndTime) acc[b.id] = { endTime: newEndTime };
      return acc;
    }, {});
    if (!Object.keys(endTimeUpdates).length) return;
    dispatch(actions.project.updateMultiBlockSynthesisStatus(endTimeUpdates));
    dispatch(actions.project.autoSaveTrigger());
  };

  // mirroring TTS handleFetchProjectWithBlockSynthesisInfo. Flips a block from
  // IN_PROGRESS to SUCCESS (with audioLink) so the play button shows.
  const handleFetchDubbingBlockSynthesisInfo = async () => {
    const selectedFields = [
      'blocks._id',
      'blocks.requestId',
      'blocks.status',
      'blocks.audioLink',
      'blocks.audioExpiredAt',
    ].join(',');
    const response = await apis.project.getProject(projectId, selectedFields);
    if (!response.status) return;
    const { project } = response.result;
    // Blocks that just received a fresh audioLink — their timeline slot is
    // realigned to the audio length below.
    const blocksGotAudio = [];
    const newBlocks = project.blocks.reduce((acc, block) => {
      const { status, audioExpiredAt, gotAudio } = resolvePolledBlockSynthesis({
        beBlock: block,
        localBlock: blocksRef.current[block.id],
        submittedRequestId: submittedRequestIdsRef.current.get(block.id),
      });
      if (gotAudio) {
        blocksGotAudio.push({ id: block.id, audioLink: block.audioLink });
        // Result arrives via polling, not the synthesis WS — track here
        trackConvertSuccess({ requestId: block.requestId, projectId });
      }
      acc[block.id] = {
        ...blocksRef.current[block.id],
        ...block,
        audioExpiredAt,
        status,
      };
      return acc;
    }, {});
    dispatch(actions.project.updateMultiBlockSynthesisStatus(newBlocks));
    dispatch(actions.project.setMultiBlockStatus(newBlocks));
    await adjustBlocksEndTimeToAudio(blocksGotAudio);
  };

  // Only blocks that are actually in the project count. A synthesis dispatch
  // that lands after its block was deleted re-creates the entry in the blocks
  // map (the reducers merge by id), and that orphan — invisible, so nothing can
  // ever settle it — would otherwise keep the whole project "synthesizing":
  // polling forever, selection locked, convert button disabled.
  const hasSynthesizingBlock = (blockIds || []).some(
    (blockId) => blocks?.[blockId]?.status === CONVERTING_STATUS.IN_PROGRESS,
  );
  // The whole-project button acts on every block, so ONE block being rewritten
  // is enough to hold it: the rewrite is about to change that block's text, and
  // the run would either skip it (leaving the project half-converted) or price
  // text that no longer exists.
  const hasRewritingBlock = (blockIds || []).some((blockId) =>
    isBlockRewriting(blocks?.[blockId]),
  );

  // In the create-audio flow, converting a block (or the whole project) has no
  // legacy `isProjectSynthesizing`/`isProjectConvertingDraft` signal, so leaving
  // the page mid-convert must be guarded by the block-level status instead.
  const isConvertingBlockAudio = canCreateBlockAudio && hasSynthesizingBlock;

  // Lock block selection (per-block checkbox + toolbar select-all) while the
  // project is converting text or downloading, so the selection can't change
  // under a running operation — it is what the operation was started on.
  //
  // An async export is not such an operation behind the export-history flag:
  // the backend snapshots the project when the export starts, and a partial
  // export carries the block ids it was started with, so a selection changed
  // afterwards cannot reach it.
  const isBlockSelectionLocked =
    (isExporting && !exportHistoryEnabled) ||
    isEditLocked ||
    (canCreateBlockAudio && hasSynthesizingBlock);

  // Clear the whole-project convert flag once synthesis settles (IN_PROGRESS →
  // idle transition), so the AudioPlayer stops showing % progress. Guarding on
  // the transition avoids clearing it in the brief window before the batched
  // IN_PROGRESS dispatch lands.
  const wasSynthesizingRef = useRef(false);
  useEffect(() => {
    if (hasSynthesizingBlock) {
      wasSynthesizingRef.current = true;
    } else if (wasSynthesizingRef.current) {
      wasSynthesizingRef.current = false;
      setIsConvertingAllBlocks(false);
    }
  }, [hasSynthesizingBlock]);

  useEffect(() => {
    if (!canCreateBlockAudio || !hasSynthesizingBlock) return undefined;
    const interval = setInterval(
      handleFetchDubbingBlockSynthesisInfo,
      FETCH_BLOCKS_STATUS_INTERVAL,
    );
    return () => clearInterval(interval);
  }, [canCreateBlockAudio, hasSynthesizingBlock]);

  const preProcessSynthesizeDubbing = async () => {
    const newBlackWordsMap = computeBlockBlackWordsMap();
    setBlockBlackWordsMap(newBlackWordsMap);
    const errorBlock = findBlockWithError();
    if (errorBlock) {
      scrollToBlock(errorBlock.id);
      return;
    }
    dispatch(actions.project.setIsPlaying(false));
    dispatch(actions.project.setIsMultiBlockMode(false));
    dispatch(actions.project.setCurrentBlockId(null));
    dispatch(actions.project.setIsPlayingBlock(false));
    if (!checkValidCreateSynthesis()) return;
    // Check and show warning for voices with creditFactor > 1
    const notShowCharsFactorDialog = localStorage.getItem('charsFactorDialog');
    if (
      !notShowCharsFactorDialog &&
      !charsFactorCheckbox &&
      !triggerBtn &&
      showWarningFactorCharcterDialog()
    ) {
      return;
    }

    if (blockWithText.length) {
      dispatch(actions.project.setIsPreProcessingSynthesis(true));
      const saved = await ensureSavedBeforeSynthesis();
      if (!saved) {
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'errorOccurred',
          }),
        );
        dispatch(actions.project.setIsPreProcessingSynthesis(false));
        return;
      }

      dispatch(actions.project.saveProject());
      dispatch(actions.project.setIsPreProcessingSynthesis(false));

      const blockCredits = {};
      for (let i = 0; i < blockWithText.length; i += 1) {
        const block = blockWithText[i];
        const credits =
          countTextLength(
            isTranslationSuccess
              ? block?.translatedText || ''
              : block?.text || '',
          ) * (block.voice?.creditFactor || 1);
        blockCredits[block.id] = credits;
      }
      dispatch(actions.project.updateAllBlocksCredits(blockCredits));

      await handleConvertAudio();
    }
  };

  const handleConfirmLeave = async (callback) => {
    if (isProjectSynthesizing) {
      onLeavePage();
      if (callback) callback(true);
      return;
    }

    const totalChars = blockIdsRef.current.reduce((sum, blockId) => {
      const block = blocksRef.current[blockId];
      const text = isTranslationSuccess
        ? block?.translatedText || ''
        : block?.text || '';
      return sum + countTextLength(text);
    }, 0);

    if (totalChars > MAX_TOTAL_CHARACTERS) {
      setOpenMaxCharsExceededOnSave(true);
      if (callback) callback(false);
      return;
    }

    const response = await saveProjectBeforeAction();
    if (response?.status) {
      if (isOpenUnsavedChangesDialog) {
        window.location.reload();
      }
      onLeavePage();
    } else {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
    }

    if (callback) callback(response?.status);
  };

  const renderOutOfCreditsDialog = () => {
    const isPackageStandardV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.STANDARD &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isPackageSpecialV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.SPECIAL &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isPackageVipV3 =
      usingPackage.level === PACKAGE_LEVEL_V3.VIP &&
      usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3);

    const isCustomPackage = checkCustomPackage({
      type: usingPackage?.type,
      level: usingPackage.level,
    });

    if (isCustomPackage) {
      return (
        <OutOfCreditsCustom
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
        />
      );
    }

    if (isPackageStandardV3) {
      return (
        <OutOfCreditsStandard
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
          // voice={voice}
        />
      );
    }

    if (isPackageSpecialV3) {
      return (
        <OutOfCreditsSpecial
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          packageCode={packageCode}
          // voice={voice}
        />
      );
    }

    if (isPackageVipV3)
      return (
        <OutOfCreditsVip
          open={openOutOfCreditsDialog}
          onClose={handleCloseOutOfCreditsDialog}
          warningOneTimeCredits={warningOneTimeCredits}
        />
      );

    // Display this popup when using a free package or a package that is neither standard nor special v3.
    return (
      <OutOfCreditsFree
        open={openOutOfCreditsDialog}
        onClose={handleCloseOutOfCreditsDialog}
      />
    );
  };

  const renderUnsavedChangesActions = () => (
    <StyledActionWarning>
      <Button
        className="btn-non-vbee-warning"
        variant="outlined"
        onClick={handleCloseUnsavedChangesDialog}
      >
        {t('stayOnPage')}
      </Button>
      <Button
        className="btn-non-vbee-warning"
        variant="contained"
        onClick={handleConfirmLeave}
      >
        {t('leaveAnyway')}
      </Button>
    </StyledActionWarning>
  );

  const handleAddNewBlock = (
    blockId,
    blockData,
    isScrollToNewBlock = false,
    // before: put the new block ahead of blockId (leading gap), not after.
    before = false,
  ) => {
    let newBlock = blockData;
    // Id of the block this call generated, returned so a caller that needs to
    // act on the new block (see handleAppendBlockFromTimeline) can address it.
    // Null when the caller supplied `blockData` — then it already knows the ids.
    let generatedBlockId = null;

    if (!blockData) {
      const newBlockId = uuidv4();
      generatedBlockId = newBlockId;
      const block = blocks[blockId];
      const defaultSpeaker = speakers?.[0];

      // Timing for the gap. When before, blockId is the NEXT block, so there's
      // no current one.
      const currentBlockIndex = blockIds.indexOf(blockId);
      const nextBlockId = blockIds[currentBlockIndex + 1];
      const nextBlock = nextBlockId ? blocks[nextBlockId] : null;

      const timingData = buildInsertedBlockTiming({
        currentBlock: before ? null : block,
        nextBlock: before ? block : nextBlock,
        blockMargin: BLOCK_MARGIN,
        // Only clamp the end block to the project duration limit for the new
        // video-dubbing create-audio flow; SRT/legacy projects keep the old
        // fixed 2s block (maxDurationSec defaults to Infinity → no clamp).
        maxDurationSec: canCreateBlockAudio
          ? VIDEO_DUBBING_MAX_DURATION_SECONDS
          : undefined,
      });

      newBlock = [
        {
          emphasis: block?.emphasis,
          hasEmphasis: block?.hasEmphasis,
          voice: block?.voice || defaultSpeaker?.voice,
          voiceCode: block?.voiceCode || defaultSpeaker?.voiceCode,
          speed: block?.speed || DEFAULT_SPEED,
          breakTime: block?.breakTime,
          elements: block?.elements,
          text: '',
          id: newBlockId,
          speakerId: block?.speakerId || defaultSpeaker?.id,
          blockStatus: BLOCK_STATUS.DRAFT,
          ...timingData,
        },
      ];

      setTimeout(() => {
        setFocusingBlockId(newBlockId);
      }, 0);

      if (!blockData && isScrollToNewBlock) {
        setTimeout(() => {
          addBlockRef.current?.[blockId]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 100);
      }
    }

    if (
      currentFilteringStatusRef.current !== BLOCK_STATUS.ALL ||
      currentFilteringSpeakerIdRef.current !== null
    ) {
      const newBlockIds = newBlock.map((block) => block.id);
      newBlockIds.forEach((id) => pendingBlockAdditions.current.add(id));
    }

    const addBlockOptions = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.ADD_BLOCK,
          },
        }
      : {};
    dispatch(
      actions.project.addBlock(blockId, newBlock, {
        ...addBlockOptions,
        before,
      }),
    );
    dispatch(actions.project.setAudioLinkSrt(null));
    dispatch(actions.project.setAudioSrtExpiredAt(null));

    return generatedBlockId;
  };

  // Timeline "Thêm khối" — append a new block after the last one, same as the
  // block list's add button (reuses handleAddNewBlock; scrolls to the new block).
  //
  // Identity-stable: it reaches the timeline as a prop, and a fresh function on
  // every render of this container was the only thing still re-rendering the
  // voice lane while typing.
  const handleAppendBlockFromTimeline = useStableCallback(() => {
    const newBlockId = handleAddNewBlock(blockIds[blockIds.length - 1], null);
    if (!newBlockId) return;
    handleFocusingBlock(newBlockId);
    scrollToBlock(newBlockId);
  });

  // Timeline mid-track "+" — insert a new block right after `afterBlockId`
  // (between it and the next), reusing the same machinery as the end "add
  // block": handleAddNewBlock already fits the new block's timing to the gap.
  const handleInsertBlockFromTimeline = useStableCallback(
    (blockId, { before = false } = {}) => {
      const newBlockId = handleAddNewBlock(blockId, null, false, before);
      if (!newBlockId) return;
      handleFocusingBlock(newBlockId);
      scrollToBlock(newBlockId);
    },
  );

  const handleConfirmDeleteSentence = (blockId) => {
    if (currentBlockId === blockId) {
      const blockWithAudio = getBlockWithAudio(blockIds, blocks);
      const index = blockWithAudio.findIndex((block) => block.id === blockId);
      const nextBlockId = blockWithAudio[index + 1]?.id;
      dispatch(actions.project.setCurrentBlockId(nextBlockId));
    }
    dispatch(actions.project.setAudioLinkSrt(null));
    dispatch(actions.project.setAudioSrtExpiredAt(null));

    const lastBlockId = blockIds.length === 1;
    const lastBlockFilteredId = filteredBlockIds.length === 1 && !lastBlockId;
    const clearsFilteredList = lastBlockId || lastBlockFilteredId;
    if (lastBlockId) {
      handleAddNewBlock();
    }

    // One undo step for the filter reset and the delete together, same as the
    // multi-block path: apart, undo restores the block under whatever filter is
    // showing now instead of the one it was deleted under, and it stays hidden.
    if (undoRedoEnabled) {
      dispatch(
        actions.history.beginTransaction(
          HISTORY_SCOPE.DUBBING,
          HISTORY_LABEL.DELETE_BLOCK,
        ),
      );
    }
    if (clearsFilteredList) {
      dispatch(actions.project.setFilterStatus(BLOCK_STATUS.ALL));
      dispatch(actions.project.setFilterVoiceCode(''));
      dispatch(actions.project.setFilterSpeakerId(null));
      dispatch(actions.project.setStartFilterBlock(true));
    }
    dispatch(actions.project.deleteBlock(blockId));
    dispatch(actions.project.unselectBlocks([blockId]));
    if (undoRedoEnabled) {
      dispatch(actions.history.endTransaction(HISTORY_SCOPE.DUBBING));
    }
    dispatch(actions.project.setIsPlaying(false));
  };

  // Handle merge block action - moved from ContentBlock for performance
  const handleMergeBlock = (blockId) => {
    const currentBlock = blocksRef.current[blockId];
    if (!currentBlock) return null;

    // Check if there's a next block
    const currentBlockIndex = blockIds.indexOf(blockId);
    const nextBlockId = blockIds[currentBlockIndex + 1];
    const nextBlock = blocksRef.current[nextBlockId];

    if (!nextBlock) return null;

    // Check if current block is synthesizing
    const isCurrentBlockSynthesizing =
      isDownloading || currentBlock.status === CONVERTING_STATUS.IN_PROGRESS;
    const isNextBlockSynthesizing =
      isDownloading || nextBlock.status === CONVERTING_STATUS.IN_PROGRESS;

    if (isCurrentBlockSynthesizing || isNextBlockSynthesizing) return null;

    // Merge text: block1 + newline + block2
    const mergedText =
      currentBlock.text && nextBlock.text
        ? `${currentBlock.text}\n${nextBlock.text}`
        : currentBlock.text || nextBlock.text || '';

    // Prepare merged block data
    const mergedBlockData = {
      text: mergedText,
      // Clear audio-related fields (from both blocks)
      status: null,
    };

    // Merge translatedText when translation is successful and blocks have translatedText
    const hasTranslatedText =
      currentBlock?.translatedText || nextBlock?.translatedText;
    if (isTranslationSuccess && hasTranslatedText) {
      mergedBlockData.translatedText =
        currentBlock.translatedText && nextBlock.translatedText
          ? `${currentBlock.translatedText}\n${nextBlock.translatedText}`
          : currentBlock.translatedText || nextBlock.translatedText || '';
    }

    // For dubbing projects, calculate merged block time:
    // startTime = first block's startTime, endTime = second block's endTime
    // This includes the gap between the two blocks
    if (currentBlock.startTime && nextBlock.endTime) {
      mergedBlockData.startTime = currentBlock.startTime;
      mergedBlockData.endTime = nextBlock.endTime;
    }

    // Compute the merged block's real status instead of hardcoding DRAFT, so the
    // filter counter matches the error icon: a merge can keep an error (e.g. a
    // black word), which a fixed DRAFT would drop from the count.
    const mergedBlock = { ...currentBlock, ...mergedBlockData };
    mergedBlockData.blockStatus = getBlockStatus({
      block: mergedBlock,
      originalBlock: originalBlocks[blockId],
      audioType,
      maxCharLimit: maxCharLimitPerBlock,
      blackWordsList: blockBlackWordsMap[blockId] || [],
      voice: resolveBlockVoice({ block: mergedBlock, speakers, voiceDefault }),
      isTranslationSuccess,
    });

    if (undoRedoEnabled) {
      dispatch(
        actions.history.beginTransaction(
          HISTORY_SCOPE.DUBBING,
          HISTORY_LABEL.MERGE_BLOCK,
        ),
      );
    }

    // Update current block with merged content
    dispatch(actions.project.updateBlock(blockId, mergedBlockData));

    // Delete next block
    dispatch(actions.project.deleteBlock(nextBlockId));

    // Unselect if next block was selected
    dispatch(actions.project.unselectBlocks([nextBlockId]));

    if (undoRedoEnabled) {
      dispatch(actions.history.endTransaction(HISTORY_SCOPE.DUBBING));
    }

    // Focus on merged block
    setTimeout(() => {
      handleFocusingBlock(blockId);
    }, 100);

    return mergedText;
  };

  const handleReorder = (list, blockId, direction) => {
    const result = [...list];
    const currentIndex = result.indexOf(blockId);
    if (direction === DIRECTION.UP) {
      const [removed] = result.splice(currentIndex, 1);
      result.splice(currentIndex - 1, 0, removed);
    } else {
      const [removed] = result.splice(currentIndex, 1);
      result.splice(currentIndex + 1, 0, removed);
    }
    return result;
  };

  const handleDragEnd = (blockId, direction) => {
    if (!direction) {
      return;
    }

    const newBlockIds = handleReorder(blockIdsRef.current, blockId, direction);
    dispatch(actions.project.updateOrderBlock(newBlockIds));
  };
  const checkRouteChange = (nextLocation) => {
    const arrayPath = nextLocation.pathname.split('/');
    const isLeavingProject =
      (arrayPath[1] === 'projects' && !arrayPath[2]) ||
      arrayPath[1] !== 'projects';
    return (
      isLeavingProject &&
      (hasUnsavedWork || isProjectSynthesizing || isConvertingBlockAudio)
    );
  };
  const handleConfirmNonVbeeDialog = async () => {
    setTriggerBtn(true);
    setOpenWrongSyntaxDialogV2(null);
  };
  const handleClickCharsFactorCheckbox = () => {
    if (!charsFactorCheckbox) {
      localStorage.setItem('nonVbeeDialog', true);
      localStorage.setItem('charsFactorDialog', true);
    } else {
      localStorage.removeItem('nonVbeeDialog');
      localStorage.removeItem('charsFactorDialog');
    }
    setCharsFactorCheckbox((value) => !value);
  };

  const handleClickBuyPackages = () => {
    history.push(ROUTES.PAYMENT);
  };

  // Combined fetching logic for both project and request status
  const handleFetchSynthesisStatus = async () => {
    if (!isProjectSynthesizing || !synthesisRequest?.id) return;

    // Fetch both project and request status in parallel
    const [projectResponse, requestResponse] = await Promise.all([
      apis.project.getProject(
        projectId,
        ['status', 'audioLink', 'audioExpiredAt'].join(','),
      ),
      apis.requests.getRequestV2(synthesisRequest.id),
    ]);

    // Update request
    if (requestResponse?.status) {
      dispatch(
        actions.synthesisRequest.updateSynthesisConfig(requestResponse.result),
      );
    }

    // Update project status
    if (projectResponse?.status) {
      const { project } = projectResponse.result;
      if (project.status === CONVERTING_STATUS.SUCCESS) {
        dispatch(actions.project.setStatus(project.status));
        dispatch(actions.project.setAudioLinkSrt(project.audioLink));
        dispatch(actions.project.setAudioSrtExpiredAt(project.audioExpiredAt));
        dispatch(actions.project.setIsProjectConvertingDraft(false));
        dispatch(actions.project.setIsProjectSynthesizing(false));
        dispatch(actions.project.setTranslationPendingSynthesis(false));
      }
    }
  };

  useEffect(() => {
    const synthesisStatusInterval = setInterval(
      handleFetchSynthesisStatus,
      FETCH_BLOCKS_STATUS_INTERVAL,
    );

    if (!isProjectSynthesizing || !synthesisRequest?.id) {
      clearInterval(synthesisStatusInterval);
    }

    return () => clearInterval(synthesisStatusInterval);
  }, [isProjectSynthesizing, synthesisRequest?.id]);

  const handleOpenTranslateErrorDialog = (description) => {
    setTranslateErrorDescription(description || t('translateFailed'));
    setOpenTranslateErrorDialog(true);
  };

  // Polling for translation progress
  const handleFetchTranslateProgress = async () => {
    if (!isTranslating || !projectId) return;

    try {
      const response = await getTranslateProgress(projectId);

      if (response?.status && response?.result?.progress) {
        const { status: translationStatus } = response.result.progress;

        if (translationStatus === TRANSLATION_STATUS.SUCCESS) {
          // Translation completed successfully
          // Keep loading overlay until getProject finishes
          const projectResponse = await apis.project.getProject(projectId);
          if (projectResponse?.status && projectResponse?.result?.project) {
            const { blocks: updatedBlocks, translation: updatedTranslation } =
              projectResponse.result.project;
            if (Array.isArray(updatedBlocks) && updatedBlocks.length > 0) {
              // Dispatch updated blocks directly from BE. The translation is
              // already persisted server-side, so skip the auto-save pass this
              // merge would otherwise trigger.
              dispatch(
                actions.project.updateMultipleBlocks(
                  updatedBlocks.map((block) => ({
                    blockId: block.id,
                    // Audio synthesized before translation no longer matches the
                    // now-translated text: reset it so the block's Play button
                    // reverts to Convert (user re-synthesizes with translatedText).
                    block: block?.translatedText
                      ? invalidateBlockAudio(block)
                      : block,
                  })),
                  autoSaveEnabled ? { skipAutoSave: true } : {},
                ),
              );
              // Sync the auto-save baseline so the merged translatedText isn't
              // re-sent on the next user edit (Backend already has it).
              if (autoSaveEnabled) {
                const blocksById = updatedBlocks.reduce((acc, block) => {
                  acc[block.id] = block;
                  return acc;
                }, {});
                dispatch(
                  actions.project.patchAutoSaveBaseline({
                    blocks: pickChangedBlocksContent(
                      updatedBlocks.map((block) => block.id),
                      blocksById,
                      PROJECT_PRODUCT.DUBBING,
                    ),
                  }),
                );
              }
            }

            // Set translation state
            if (updatedTranslation) {
              dispatch(actions.project.setTranslation(updatedTranslation));
              dispatch(actions.project.setTranslationPendingSynthesis(true));
              if (undoRedoEnabled) {
                dispatch(actions.history.clear(HISTORY_SCOPE.DUBBING));
              }
              // Audio generated before translation is no longer valid for translated text.
              dispatch(actions.project.setAudioLinkSrt(null));
              dispatch(actions.project.setAudioSrtExpiredAt(null));
            }
          }

          // Hide loading overlay after everything is done
          setIsTranslating(false);
          dispatch(actions.project.setIsTranslating(false));
        } else if (translationStatus === TRANSLATION_STATUS.FAILURE) {
          // Translation failed
          if (translation) {
            dispatch(
              actions.project.setTranslation({
                ...translation,
                status: TRANSLATION_STATUS.FAILURE,
              }),
            );
          }
          setIsTranslating(false);
          dispatch(actions.project.setIsTranslating(false));
          handleOpenTranslateErrorDialog(t('translateFailed'));
        }
        // If PENDING, continue polling
      }
    } catch (error) {
      if (translation) {
        dispatch(
          actions.project.setTranslation({
            ...translation,
            status: TRANSLATION_STATUS.FAILURE,
          }),
        );
      }
      setIsTranslating(false);
      dispatch(actions.project.setIsTranslating(false));
      handleOpenTranslateErrorDialog(t('translateFailed'));
    }
  };

  // Resume translation UI/polling after page reload/navigation if BE job is still pending.
  useEffect(() => {
    if (translation?.status === TRANSLATION_STATUS.PENDING) {
      translationIdRef.current = translation?.id || translationIdRef.current;
      setIsTranslating(true);
      dispatch(actions.project.setIsTranslating(true));
    }
  }, [translation?.status, translation?.id, dispatch]);

  useEffect(() => {
    const translateProgressInterval = setInterval(
      handleFetchTranslateProgress,
      FETCH_TRANSLATE_STATUS_INTERVAL,
    );

    if (!isTranslating) {
      clearInterval(translateProgressInterval);
    }

    return () => clearInterval(translateProgressInterval);
  }, [isTranslating]);

  // Effect 1: Handle filter selection changes (re-filter blocks)
  useEffect(() => {
    const filterBlocks = () => {
      if (!startFilterBlock) return;

      const isNoFilter =
        currentFilteringStatus === BLOCK_STATUS.ALL &&
        currentFilteringSpeakerId === null;

      if (isNoFilter) {
        setFilteredBlockIds(blockIds);
        setFilterBlockResults(blocks);
        return;
      }

      // Helper: returns whether a block matches current filter status
      const matchesStatus = (blockId) => {
        if (currentFilteringStatus === BLOCK_STATUS.ALL) return true;
        const block = blocks[blockId];
        const originalBlock = originalBlocks[blockId];
        const blockStatus = getBlockStatus({
          block,
          originalBlock,
          audioType,
          maxCharLimit: maxCharLimitPerBlock,
          blackWordsList: blockBlackWordsMap[blockId] || [],
          voice: resolveBlockVoice({ block, speakers, voiceDefault }),
          isTranslationSuccess,
        });
        return blockStatus === currentFilteringStatus;
      };

      // Helper: returns whether a block matches current filter speaker
      const matchesSpeaker = (blockId) => {
        if (currentFilteringSpeakerId === null) return true;

        return blocks[blockId]?.speakerId === currentFilteringSpeakerId;
      };

      const filteredIds = blockIds.filter(
        (blockId) => matchesStatus(blockId) && matchesSpeaker(blockId),
      );

      // Create filtered blocks object from filteredIds
      const filteredBlocks = Object.fromEntries(
        filteredIds.map((id) => [id, blocks[id]]),
      );

      setFilteredBlockIds(filteredIds);
      setFilterBlockResults(filteredBlocks);

      const filteredBlockSelected = Object.keys(filteredBlocks)?.filter(
        (blockId) => selectedBlockIds?.includes(blockId),
      );
      const unSelectedBlockIds = selectedBlockIds?.filter(
        (blockId) => !filteredBlockSelected?.includes(blockId),
      );
      dispatch(actions.project.unselectBlocks(unSelectedBlockIds));

      dispatch(actions.project.setStartFilterBlock(false));
    };

    filterBlocks();
  }, [
    currentFilteringStatus, // Only trigger when filter selection changes
    startFilterBlock,
    currentFilteringSpeakerId,
    blocks,
    blockIds,
    selectedBlockIds,
  ]);

  // Effect 2: Update filtered block content when blocks are edited (without changing which blocks are filtered)
  useEffect(() => {
    const updateFilteredBlocks = () => {
      if (
        currentFilteringStatus === BLOCK_STATUS.ALL &&
        currentFilteringSpeakerId === null
      ) {
        setFilteredBlockIds(blockIds);
        setFilterBlockResults(blocks);
        // Clear any pending additions when not filtering
        pendingBlockAdditions.current.clear();
        return;
      }

      // Handle pending block additions
      if (pendingBlockAdditions.current.size > 0) {
        const newIds = Array.from(pendingBlockAdditions.current);
        const validNewIds = newIds.filter((id) => blocks[id]); // Only include blocks that exist

        if (validNewIds.length > 0) {
          setFilteredBlockIds((prev) => {
            // Use the actual blockIds order from Redux to maintain correct positioning
            const newFiltered = blockIds.filter(
              (id) => prev.includes(id) || validNewIds.includes(id),
            );
            return newFiltered;
          });

          setFilterBlockResults((prev) => {
            const updated = { ...prev };
            validNewIds.forEach((blockId) => {
              updated[blockId] = blocks[blockId];
            });
            return updated;
          });

          // Clear processed additions
          validNewIds.forEach((id) => pendingBlockAdditions.current.delete(id));
        }
        return;
      }

      // Update the content of currently filtered blocks without changing which blocks are included
      const updatedFilteredBlocks = {};

      filteredBlockIds.forEach((blockId) => {
        if (blocks[blockId]) {
          updatedFilteredBlocks[blockId] = blocks[blockId];
        }
      });

      // Only update if we have valid blocks and the content has actually changed
      setFilterBlockResults((prev) => ({
        ...prev,
        ...updatedFilteredBlocks,
      }));

      // Handle case where blocks were deleted - remove them from filtered list
      const validFilteredIds = filteredBlockIds.filter(
        (blockId) => blocks[blockId],
      );
      if (validFilteredIds.length !== filteredBlockIds.length) {
        setFilteredBlockIds(validFilteredIds);
      }
    };

    updateFilteredBlocks();
  }, [
    blocks,
    blockIds,
    filteredBlockIds,
    currentFilteringStatus,
    currentFilteringSpeakerId,
    pendingBlockAdditions,
    setFilteredBlockIds,
    setFilterBlockResults,
  ]);

  // `speakers` is fetched after the project loads, so the statuses stored at
  // load time were resolved without them — a block reading with its speaker's
  // voice was scored against `voiceDefault`. Recompute once they land, else the
  // status counts in the filter dropdown disagree with the list itself.
  useEffect(() => {
    dispatch(actions.project.setMultiBlockStatus(blocks));
  }, [audioType, speakers]);

  useEffect(() => {
    if (triggerBtn && !isSkeletonUI) {
      preProcessSynthesizeDubbing();
      setTriggerBtn(false);
    }
  }, [triggerBtn, isSkeletonUI]);

  const renderCloseButtonV2 = (handleClose) => {
    switch (openWrongSyntaxDialogV2?.type) {
      case DIALOG_V2_TYPE.NON_VBEE_WARNING:
        return (
          <div className="non-vbee-warning">
            <FormControlLabel
              control={
                <Checkbox
                  classes={{ root: 'checkbox' }}
                  color="iconPrimary"
                  value={charsFactorCheckbox}
                  onChange={handleClickCharsFactorCheckbox}
                />
              }
              label={
                <Typography className="not-show-again-text">
                  {t('notShowAgain')}
                </Typography>
              }
              labelPlacement={t('notShowAgain')}
            />
            <div className="">
              <Button
                className="btn-non-vbee-warning"
                variant="outlined"
                onClick={handleClose}
              >
                {t('cancel')}
              </Button>
              <Button
                className="btn-non-vbee-warning"
                variant="contained"
                onClick={handleConfirmNonVbeeDialog}
              >
                {t('continueGenerate')}
              </Button>
            </div>
          </div>
        );
      case DIALOG_V2_TYPE.CANNOT_USE_VOICE:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              setOpenWrongSyntaxDialogV2(null);
            }}
          >
            {t('gotItV2')}
          </Button>
        );
      case DIALOG_V2_TYPE.RENAME_VOICE:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              setOpenWrongSyntaxDialogV2(null);
            }}
          >
            {t('gotIt')}
          </Button>
        );
      case DIALOG_V2_TYPE.BLOCK_SYNTHESIS:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={() => {
              window.open(`${LANDING_PAGE_URL}/support`, '_blank');
            }}
          >
            {t('submitSupportRequest')}
          </Button>
        );
      default:
        return (
          <Button
            className="btn-understood"
            variant="contained"
            onClick={handleClickBuyPackages}
          >
            {t('buyPackages')}
          </Button>
        );
    }
  };

  const renderLockedCycleButton = () => (
    <Button variant="contained" onClick={() => setOpenDialogLockedCycle(null)}>
      {t('understoodV3')}
    </Button>
  );
  const renderCloseButton = (handleClose, labelKey) => (
    <Button
      variant="contained"
      onClick={handleClose}
      sx={{
        width: '98px',
        height: '40px',
        textTransform: 'none',
        marginRight: '12px',
        borderRadius: '8px',
      }}
    >
      {t(labelKey)}
    </Button>
  );

  const renderChangeFormatButton = () => (
    <div className="change-format-button">
      <Button
        className="btn btn-cancel"
        variant="outlined"
        onClick={() =>
          dispatch(actions.project.setOpenDialogChangeFormat(false))
        }
      >
        {t('cancel')}
      </Button>
      <Button
        className="btn btn-agree"
        variant="contained"
        onClick={() => {
          dispatch(
            actions.project.setAudioType(audioType === 'mp3' ? 'wav' : 'mp3'),
          );
          dispatch(actions.project.setOpenDialogChangeFormat(false));
          dispatch(actions.project.setIsPlaying(false));
          dispatch(actions.project.setIsMultiBlockMode(false));
          dispatch(actions.project.setIsPlayingBlock(false));
        }}
      >
        {t('agree')}
      </Button>
    </div>
  );

  useEffect(
    () => () => {
      pendingBlockAdditions.current.clear();
    },
    [],
  );

  // Handle delete block
  const blocksSelected = useMemo(
    () => selectedBlockIds.map((blockId) => blocks[blockId]),
    [blockIds, blocks, selectedBlockIds],
  );
  const isSynthesizing =
    deleteDialogData === true
      ? blocksSelected.some(
          (block) => block?.blockStatus === BLOCK_STATUS.IN_PROGRESS,
        )
      : blocks?.[deleteDialogData]?.blockStatus === BLOCK_STATUS.IN_PROGRESS;

  const handleUpdateProject = async (blockIdsDeleted) => {
    // Under auto-save the block deletion is already persisted by the engine (the
    // deleteBlock(s) dispatch triggers an auto-save pass), so this legacy full
    // save is redundant.
    if (autoSaveEnabled) return;
    try {
      // TODO: Handle check if there is any block that is error then disable saving project
      const videoSettingsPayload = buildVideoSettingsPayload({
        aspectRatioEnabled: videoDubbingAspectRatioEnabled,
        backgroundColorEnabled: videoDubbingBgColorEnabled,
        videoSettings,
      });
      await updateProject(
        projectId,
        buildProjectPayload({
          title,
          blockIds: multiBlock?.blockIds?.filter(
            (blockId) => !blockIdsDeleted?.includes(blockId),
          ),
          blocks,
          voiceDefault,
          audioType,
          videoSettings: videoSettingsPayload,
        }),
      );
    } catch {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
    }
  };

  const handleDeleteBlockSelected = () => {
    const isDeleteAll = blockIds.length === selectedBlockIds?.length;
    const isDeleteAllFiltered =
      filteredBlockIds.length === selectedBlockIds?.length && !isDeleteAll;

    // Deleting every block the filter shows would leave an empty list, so the
    // filter is dropped — every one of them, or the ones left behind keep
    // hiding what survived.
    const clearsFilteredList = isDeleteAll || isDeleteAllFiltered;

    if (isDeleteAll) {
      handleAddNewBlock();

      apis.project.deleteTranslation(projectId);
      dispatch(actions.project.clearTranslation());
      if (undoRedoEnabled) {
        // Must stay outside the transaction below — HISTORY_CLEAR drops any
        // open one, which would leave the delete unrecorded.
        dispatch(actions.history.clear(HISTORY_SCOPE.DUBBING));
      }
    }
    const blockIdsToDelete = selectedBlockIds;
    dispatch(actions.project.setCurrentBlockId(null));

    // The filter reset and the delete are one user action. Recorded apart, the
    // reset lands outside the entry and undo puts the blocks back under the
    // filter that is showing now, not the one they were deleted under — so
    // they stay hidden. A transaction makes the pair a single undo step.
    if (undoRedoEnabled) {
      dispatch(
        actions.history.beginTransaction(
          HISTORY_SCOPE.DUBBING,
          HISTORY_LABEL.DELETE_BLOCKS,
        ),
      );
    }
    if (clearsFilteredList) {
      dispatch(actions.project.setFilterStatus(BLOCK_STATUS.ALL));
      dispatch(actions.project.setFilterVoiceCode(''));
      dispatch(actions.project.setFilterSpeakerId(null));
      dispatch(actions.project.setStartFilterBlock(true));
    }
    dispatch(actions.project.deleteBlocks(blockIdsToDelete));
    if (undoRedoEnabled) {
      dispatch(actions.history.endTransaction(HISTORY_SCOPE.DUBBING));
    }
    dispatch(actions.project.setIsPlaying(false));
    dispatch(actions.project.setAudioLinkSrt(null));
    dispatch(actions.project.setAudioSrtExpiredAt(null));
    handleUpdateProject(blockIdsToDelete ?? []);
    setDeleteDialogData(false);
  };

  const handleDeleteSingleBlock = (blockId) => {
    handleConfirmDeleteSentence(blockId);
    handleUpdateProject([deleteDialogData]);
    setDeleteDialogData(false);
  };

  const handleDeleteMultiBlock = () => {
    if (deleteDialogData === true) {
      handleDeleteBlockSelected();
    } else {
      // Delete single block with id
      handleDeleteSingleBlock(deleteDialogData);
    }
  };

  const descriptionDeleteBlockKeyName = useMemo(() => {
    if (isSynthesizing) {
      return 'descriptionDeleteBlockPending';
    }

    if (deleteDialogData === true) {
      return selectedBlockIds.length === 1
        ? 'areYouSureWantToDeleteBlock'
        : 'areYouSureWantToDeleteBlocks';
    }
    return 'areYouSureWantToDeleteBlock';
  }, [isSynthesizing, selectedBlockIds]);

  const numberOfBlocksToDelete = useMemo(() => {
    if (deleteDialogData === true) {
      return selectedBlockIds.length === blockIds.length
        ? t('allBlocks')
        : selectedBlockIds.length;
    }
    return blockIds.length === 1 ? t('allBlocks') : 1;
  }, [deleteDialogData, selectedBlockIds]);

  // Calculate costs credit for all blocks without audio link
  const blockWithoutAudioLink = blockWithText.filter((block) =>
    [
      BLOCK_STATUS.DRAFT,
      BLOCK_STATUS.ERROR,
      BLOCK_STATUS.AUDIO_EXPIRED,
      BLOCK_STATUS.IN_PROGRESS,
    ]?.includes(block?.blockStatus),
  );
  const costsCredit = useMemo(
    () =>
      blockWithoutAudioLink.reduce(
        (acc, block) =>
          acc +
          (block?.voice?.creditFactor || voiceDefault?.creditFactor) *
            countTextLength(
              isTranslationSuccess
                ? block?.translatedText || ''
                : block?.text || '',
            ),
        0,
      ),
    [blockWithoutAudioLink, voiceDefault, isTranslationSuccess],
  );

  const handleOpenPopupNotEnoughCredits = useCallback(() => {
    setOpenPopupNotEnoughCredits(true);
  }, []);

  const handleClosePopupNotEnoughCredits = useCallback(() => {
    setOpenPopupNotEnoughCredits(false);
  }, []);

  const handleGetRewriteProgress = async () => {
    try {
      const progress = await getRewriteProgress(projectId);
      const rewriteBlockss = progress?.result?.progress?.blocks || [];
      if (rewriteBlockss?.length > 0) {
        // Polling if any block has SUCCESS status
        const hasSuccessBlock = rewriteBlockss.some(
          (item) => item?.rewrite?.status === REWRITE_STATUS.PENDING,
        );

        // Rewrite draft (`rewrite` field) is not content — skip auto-save so the
        // progress polls don't flicker the save status. The applied rewrite is
        // persisted later via UPDATE_BLOCK when the user clicks "Replace".
        dispatch(
          actions.project.updateMultipleBlocks(
            rewriteBlockss.map((block) => ({
              blockId: block.id,
              block: {
                rewrite: block.rewrite,
                blockStatus: blocksRef.current[block.id]?.blockStatus,
              },
            })),
            { skipAutoSave: true },
          ),
        );

        if (hasSuccessBlock) {
          const pollInterval = setInterval(async () => {
            try {
              const updatedProgress = await getRewriteProgress(projectId);

              const updatedRewriteBlocks =
                updatedProgress?.result?.progress?.blocks || [];
              if (updatedRewriteBlocks?.length > 0) {
                // Stop polling if no SUCCESS blocks remain
                const stillHasSuccessBlock = updatedRewriteBlocks.some(
                  (item) => item?.rewrite?.status === REWRITE_STATUS.PENDING,
                );
                if (!stillHasSuccessBlock) {
                  dispatch(
                    actions.project.updateMultipleBlocks(
                      updatedRewriteBlocks.map((block) => ({
                        blockId: block.id,
                        block: {
                          rewrite: block.rewrite,
                          // Keep the real status (see note above).
                          blockStatus: blocksRef.current[block.id]?.blockStatus,
                        },
                      })),
                      { skipAutoSave: true },
                    ),
                  );
                  clearInterval(pollInterval);
                }
              }
            } catch (error) {
              clearInterval(pollInterval);
            }
          }, 2000);
        }
      }
    } catch (error) {
      // TODO: Handle error
    }
  };

  // TODO: Handle rewrite progress
  //
  // `optimistic` is the PENDING a block sets on itself before its rewrite
  // request has landed (see handleRewrite in ContentBlock). Polling on that one
  // asks the server about a job it does not have yet: it answers with the
  // PREVIOUS job, and the dispatch below overwrites the fresh PENDING with that
  // stale status — the rewrite guard drops while the request is still in flight,
  // and nothing ever starts the interval because the status never changes again.
  // So wait for the confirmed PENDING. The dep is the pair, not the status
  // alone, or the optimistic → confirmed transition would not re-run this.
  useEffect(() => {
    if (
      projectId &&
      Object.values(blocks)?.some(
        (block) =>
          block?.rewrite?.status === REWRITE_STATUS.PENDING &&
          !block?.rewrite?.optimistic,
      ) &&
      rewriteEnabled
    ) {
      handleGetRewriteProgress();
    }
  }, [
    projectId,
    ...Object.values(blocks)?.map(
      (block) =>
        `${block?.rewrite?.status || ''}:${
          block?.rewrite?.optimistic ? 'o' : ''
        }`,
    ),
    rewriteEnabled,
  ]);
  const handleOpenTranslateDialog = () => {
    if (!hasTranslatePermission) {
      setOpenPopupUpPkgUseRewrite(true);
      return;
    }
    if (hasProjectAudio) {
      setTranslateWarningType(
        TRANSLATE_WARNING_TYPE.INITIAL_TRANSLATION_WITH_AUDIO,
      );
      setOpenTranslateConfirmDialog(true);
      return;
    }
    setOpenTranslateDialog(true);
  };

  const handleOpenTranslateOtherLanguage = () => {
    if (!hasTranslatePermission) {
      setOpenPopupUpPkgUseRewrite(true);
      return;
    }
    setTranslateWarningType(TRANSLATE_WARNING_TYPE.OTHER_LANGUAGE_TRANSLATION);
    setOpenTranslateConfirmDialog(true);
  };

  const handleConfirmOpenTranslateDialog = () => {
    setOpenTranslateConfirmDialog(false);
    setTranslateWarningType(null);
    setOpenTranslateDialog(true);
  };

  const renderTranslateConfirmActions = () => (
    <StyledActionWarning>
      <CommonButton
        customStyle={{
          padding: '8px 24px',
          borderRadius: '8px',
          height: '40px',
        }}
        variant="outlineSmall"
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.darkBlue,
        }}
        onClick={() => setOpenTranslateConfirmDialog(false)}
      >
        {t('cancel')}
      </CommonButton>
      <CommonButton
        customStyle={{
          padding: '8px 24px',
          borderRadius: '8px',
          height: '40px',
        }}
        variant="contained"
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.darkBlue,
        }}
        onClick={handleConfirmOpenTranslateDialog}
      >
        {t('continueTranslate')}
      </CommonButton>
    </StyledActionWarning>
  );

  let translateWarningText = t('translateOtherLanguageNoAudioWarning');
  if (
    translateWarningType ===
    TRANSLATE_WARNING_TYPE.INITIAL_TRANSLATION_WITH_AUDIO
  ) {
    translateWarningText = t('translateHasAudioWarning');
  } else if (hasProjectAudio) {
    translateWarningText = t('translateOtherLanguageHasAudioWarning');
  }

  // Actual measured height of the timeline dock (0 until first measure).
  const [timelineHeight, setTimelineHeight] = useState(0);

  // Height reserved below the editor: the timeline dock (when shown) replaces
  // the AudioPlayer; filtering/selecting hides the player and reserves nothing.
  // When shown, reserve the timeline's REAL height so the editor + dock fit the
  // viewport — collapsed = toolbar only, expanded = toolbar + tracks. Falls back
  // to the collapsed height (TIMELINE_PANEL_HEIGHT) until it has been measured.
  const bottomReserveHeight = useMemo(() => {
    let bottomHeight = AUDIO_PLAYER_HEIGHT;
    if (timelineTracksEnabled) {
      // `- 12`: empirical offset. The measured dock height over-reserves by
      // ~12px, otherwise a thin gray strip shows below the dock at the viewport
      // bottom.
      bottomHeight = (timelineHeight || TIMELINE_PANEL_HEIGHT) - 12;
    } else if (
      isFilteringBlockStatus ||
      hasSelectedBlock ||
      isFilteringBlockSpeaker
    ) {
      bottomHeight = 0;
    }
    return bottomHeight;
  }, [
    timelineTracksEnabled,
    timelineHeight,
    isFilteringBlockStatus,
    isFilteringBlockSpeaker,
    hasSelectedBlock,
  ]);

  const timelineCreateAudioDisabled =
    isAutoSaveOverLimit ||
    // Covers the skeleton UI and every download/export in flight: synthesizing
    // mid-export would change the audio the export is already building from.
    isEditLocked ||
    // Empty already means "nothing left to submit": in-flight blocks are
    // filtered out upstream.
    !timelineConvertBlocks.length ||
    hasRewritingBlock;
  const isTimelineAudioChanging = isConvertingAllBlocks || hasSynthesizingBlock;

  // An end block can only be appended while the
  // project is still under the duration limit (total < 90m - margin). This
  // only applies to the new video-dubbing create-audio flow; SRT/legacy
  // projects (or when the flag is off) keep the old unlimited behavior.
  const projectTotalDurationSec = getBlocksMaxEndSec(blocks, blockIds);
  const isAddEndBlockOverLimit =
    canCreateBlockAudio &&
    !canAppendEndBlock(
      projectTotalDurationSec,
      VIDEO_DUBBING_MAX_DURATION_SECONDS,
    );

  // ── Stable props for the virtualized block list ─────────────────────────────
  // Rows are memoized and compare props by reference, and react-window hands
  // them one `itemData` object — so an inline object/array or a freshly created
  // handler here re-renders every mounted block on every render of this
  // component. Handlers get a fixed identity through useStableCallback (which
  // still invokes the latest closure, so no stale state), letting the two
  // objects below be memoized on plain values only.
  const stableChangeCharacterExceed = useStableCallback(
    handleChangeCharacterExceed,
  );
  const stableAddNewParagraphs = useStableCallback(handleAddNewBlock);
  const stableSelectParagraphId = useStableCallback(
    handleConfirmDeleteSentence,
  );
  const stableOpenDiscardedVoiceCloningDialog = useStableCallback(
    handleOpenDiscardedVoiceCloningDialog,
  );
  const stableFocusingBlock = useStableCallback(handleFocusingBlock);
  const stableDragEnd = useStableCallback(handleDragEnd);
  const stableSaveProject = useStableCallback(saveProject);
  const stableConvertBlockToAudio = useStableCallback(
    handleConvertSingleBlockDubbing,
  );
  const stablePlayAudioInBlock = useStableCallback(handlePlayAudioInBlock);
  const stableSeekedInBlock = useStableCallback(handleSeekedInBlock);
  const stableCheckValidCreateSynthesis = useStableCallback(
    checkValidCreateSynthesis,
  );
  const stableMergeBlock = useStableCallback(handleMergeBlock);
  const stableAddEndBlock = useStableCallback(() =>
    handleAddNewBlock(
      filteredBlockIds[filteredBlockIds?.length - 1],
      null,
      true,
    ),
  );

  // `canCreateBlockAudio` still gates these two: passing `undefined` is what
  // tells a block it cannot create/play its own audio, so the conditional is
  // kept rather than folded into the stable wrappers.
  const renderContentBlockProps = useMemo(
    () => ({
      audioType,
      characterExceed,
      onChangeCharacterExceed: stableChangeCharacterExceed,
      onAddNewParagraphs: stableAddNewParagraphs,
      onSelectParagraphId: stableSelectParagraphId,
      onOpenDiscardedVoiceCloningDialog: stableOpenDiscardedVoiceCloningDialog,
      focusingBlockId,
      onFocusingBlock: stableFocusingBlock,
      onDragEnd: stableDragEnd,
      onSaveProject: stableSaveProject,
      voiceDefault,
      isDownloading: isEditLocked,
      onOpenOutOfCreditsDialog: setOpenOutOfCreditsDialog,
      onConvertBlockToAudio: canCreateBlockAudio
        ? stableConvertBlockToAudio
        : undefined,
      enableDubbingBlockAudio: canCreateBlockAudio,
      canDownloadBlockAudio,
      handlePlayAudio: canCreateBlockAudio ? stablePlayAudioInBlock : undefined,
      onSeekInBlock: stableSeekedInBlock,
      blockAudioCurrentTime,
      blockAudioDuration,
      checkValidCreateSynthesis: stableCheckValidCreateSynthesis,
      onOpenWrongSyntaxDialog: setOpenWrongSyntaxDialog,
      onOpenWrongSyntaxDialogV2: setOpenWrongSyntaxDialogV2,
      projectId,
      isPlayingBlock,
      currentPlayingBlockId,
      scrollToBlockId,
      scrollToBlockAlign,
      isPlaying,
      onMergeBlock: stableMergeBlock,
      speakers: speakers || [],
      isBlockUIImprovement,
      enableCreditUsagePreview,
      isBlockSelectionLocked,
      // Only a video-dubbing project with the timeline hands focus over to the
      // playhead while playing — every other project keeps click-to-focus.
      hasVideoTimeline: timelineTracksEnabled,
    }),
    [
      audioType,
      characterExceed,
      stableChangeCharacterExceed,
      stableAddNewParagraphs,
      stableSelectParagraphId,
      stableOpenDiscardedVoiceCloningDialog,
      focusingBlockId,
      stableFocusingBlock,
      stableDragEnd,
      stableSaveProject,
      voiceDefault,
      isEditLocked,
      setOpenOutOfCreditsDialog,
      canCreateBlockAudio,
      stableConvertBlockToAudio,
      canDownloadBlockAudio,
      stablePlayAudioInBlock,
      stableSeekedInBlock,
      blockAudioCurrentTime,
      blockAudioDuration,
      stableCheckValidCreateSynthesis,
      setOpenWrongSyntaxDialog,
      setOpenWrongSyntaxDialogV2,
      projectId,
      isPlayingBlock,
      currentPlayingBlockId,
      scrollToBlockId,
      scrollToBlockAlign,
      isPlaying,
      stableMergeBlock,
      speakers,
      isBlockUIImprovement,
      enableCreditUsagePreview,
      isBlockSelectionLocked,
      timelineTracksEnabled,
    ],
  );

  const addButtonProps = useMemo(
    () => ({
      disabled: isProjectSynthesizing || isSkeletonUI || isAddEndBlockOverLimit,
      onClick: stableAddEndBlock,
      text: t('addMoreBlock'),
      icon: addBlockIcon,
    }),
    [
      isProjectSynthesizing,
      isSkeletonUI,
      isAddEndBlockOverLimit,
      stableAddEndBlock,
      t,
    ],
  );

  return (
    <>
      {autoSaveEnabled && <AutoSaveBanner />}
      {showExportBanner && (
        <ExportingBanner isExporting={isExporting} formats={exportFormats} />
      )}
      <StyledContent
        ref={contentRef}
        sidebarWidth={getSidebarWidth()}
        isShowHeaderBanner={isShowHeaderBanner}
        hasCharacterLimitBanner={isAutoSaveOverLimit}
        isFilteringBlockStatus={
          isFilteringBlockStatus || isFilteringBlockSpeaker
        }
        hasSelectedBlock={hasSelectedBlock}
        onClick={handleStopFocusOnBlock}
        role="button"
        tabIndex="-1"
      >
        <PanelGroup
          direction="horizontal"
          autoSaveId="dubbing-panels"
          style={{
            height: `calc(100vh - ${HEADER_HEIGHT}px - ${bottomReserveHeight}px - ${
              isShowHeaderBanner ? FLASH_SALE_BANNER_HEIGHT : 0
            }px - ${
              isAutoSaveOverLimit ? CHARACTER_LIMIT_BANNER_HEIGHT : 0
            }px - ${showExportBanner ? EXPORT_BANNER_HEIGHT : 0}px)`,
          }}
        >
          <Panel
            ref={blockPanelRef}
            defaultSize={60}
            minSize={isCaptionPanelOpen ? 10 : 40}
            style={{
              minWidth: isCaptionPanelOpen ? CAPTION_PANEL_WIDTH : 720,
              maxWidth: isCaptionPanelOpen ? CAPTION_PANEL_WIDTH : undefined,
            }}
          >
            <div
              onClick={(e) => {
                e.stopPropagation();
              }}
              role="button"
              tabIndex="-1"
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                // Anchors the extraction overlay below (position: absolute;
                // inset: 0) to the block list column only.
                position: 'relative',
              }}
            >
              {isCaptionPanelOpen ? (
                <CaptionPanel isLocked={isEditLocked} />
              ) : (
                <>
                  {showBlockScopedExtractLoading && (
                    <AILoadingOverlay text={t('aiProcessing')} />
                  )}
                  <Toolbar
                    setDeleteDialog={setDeleteDialogData}
                    filterBlockResults={Object.fromEntries(
                      filteredBlockIds.map((id) => [
                        id,
                        filterBlockResults[id],
                      ]),
                    )}
                    onOpenTranslateDialog={handleOpenTranslateDialog}
                    onOpenTranslateOtherLanguage={
                      handleOpenTranslateOtherLanguage
                    }
                    projectId={projectId}
                    isSkeletonUI={isSkeletonUI}
                    handleConvertBlockSelected={
                      canCreateBlockAudio
                        ? handleRequestConvertBlockSelected
                        : undefined
                    }
                    canDownloadBlockAudio={canDownloadBlockAudio}
                  />
                  <Divider sx={{ color: COLOR.indigo[16] }} />
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <VirtualizedContentList
                      saveProject={stableSaveProject}
                      onOpenPopupNotEnoughCredits={
                        handleOpenPopupNotEnoughCredits
                      }
                      setDeleteDialogData={setDeleteDialogData}
                      blockIds={filteredBlockIds}
                      blockBlackWordsMap={blockBlackWordsMap}
                      blocks={filterBlockResults}
                      originalBlocks={originalBlocks}
                      // Full unfiltered collections the rows need for gap /
                      // consecutive checks. Each row used to read these from
                      // the store itself, which re-rendered every row on any
                      // edit.
                      allBlockIds={blockIds}
                      allBlocks={blocks}
                      isFiltering={
                        isFilteringBlockStatus || isFilteringBlockSpeaker
                      }
                      containerHeight="100%"
                      isSkeletonUI={isSkeletonUI}
                      renderContentBlockProps={renderContentBlockProps}
                      showAddButton={
                        !isFilteringBlockStatus && !isFilteringBlockSpeaker
                      }
                      addButtonProps={addButtonProps}
                    />
                  </div>
                </>
              )}
            </div>
          </Panel>

          {shouldShowVideoPanel && (
            <>
              <PanelResizeHandle
                className="resize-handle"
                style={{ display: isVideoExpanded ? 'none' : undefined }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="handle-indicator">
                  <div className="handle-line" />
                  <div className="handle-line" />
                </div>
              </PanelResizeHandle>
              <Panel defaultSize={40} minSize={20} style={{ minWidth: 400 }}>
                <div
                  data-preview-panel
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                  style={{
                    height: '100%',
                    ...(isVideoExpanded
                      ? {
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 5,
                        }
                      : {}),
                  }}
                >
                  <VideoPreviewPanel
                    ref={videoRef}
                    videoUrl={videoUrl}
                    isExpanded={isVideoExpanded}
                    onToggleExpand={handleToggleVideoExpand}
                    aspectRatioEnabled={videoDubbingAspectRatioEnabled}
                    bgColorEnabled={videoDubbingBgColorEnabled}
                    aspectRatio={videoSettings?.aspectRatio}
                    onAspectRatioChange={handleAspectRatioChange}
                    backgroundColor={videoSettings?.backgroundColor}
                    onBackgroundColorChange={handleBackgroundColorChange}
                    settingsDisabled={isVideoExporting}
                    showBackgroundOnly={Boolean(isBeyondVideo)}
                    captionOverlayEnabled={captionEnabled}
                    isPlaying={isPlaying}
                    getCurrentSec={getCurrentSec}
                    currentSec={currentTime}
                  />
                </div>
              </Panel>
            </>
          )}
        </PanelGroup>
      </StyledContent>

      {timelineTracksEnabled && (
        <DubbingTimeline
          durationSec={duration}
          onHeightChange={setTimelineHeight}
          currentSec={currentTime}
          isPlaying={isPlaying}
          getCurrentSec={getCurrentSec}
          onPlayPause={isPlaying ? handlePause : handlePlay}
          onSeek={handleSeeked}
          isAudioChanging={isTimelineAudioChanging}
          hasProjectAudio={
            canCreateBlockAudio ? isTimelineReSynthesize : hasProjectAudio
          }
          createAudioTooltip={t('costsCredit', {
            value: timelineConvertCredits?.toLocaleString(language),
          })}
          onCreateAudio={handleConvertAllBlocksDubbing}
          isCreateAudioDisabled={timelineCreateAudioDisabled}
          // The button converts the WHOLE project, so it steps aside while a
          // filter narrows the editor to a subset — it would otherwise spend
          // credits on blocks the user isn't looking at. Hidden in place, so the
          // toolbar doesn't reflow.
          isCreateAudioHidden={
            isFilteringBlockStatus || isFilteringBlockSpeaker
          }
          isLocked={isEditLocked}
          isConverting={isConvertingAllBlocks}
          synthesisProgress={dubbingSynthesisProgress}
          onAddBlock={handleAppendBlockFromTimeline}
          onInsertBlock={handleInsertBlockFromTimeline}
          isAddBlockDisabled={
            isProjectSynthesizing || isExtracting || isAddEndBlockOverLimit
          }
          blackWordsList={blackWordsList}
          isExtracting={isExtracting}
          revealBlockId={revealClipId}
          onSelectBlock={handleSelectBlockFromTrack}
        />
      )}

      {!timelineTracksEnabled &&
        !isFilteringBlockStatus &&
        !hasSelectedBlock &&
        !isFilteringBlockSpeaker && (
          <AudioPlayer
            enableCreditUsagePreview={enableCreditUsagePreview}
            costsCredit={costsCredit}
            isPlayAudio={
              canCreateBlockAudio
                ? isDubbingBlockAudioReady && !blockWithTextChanged?.length
                : !isChangedAudioType &&
                  !!audioLinkSrt &&
                  !!blockWithText.length &&
                  !blockWithTextChanged?.length &&
                  !hasAudioExpired &&
                  !isTranslationPendingSynthesis
            }
            handleConvertAudio={
              canCreateBlockAudio
                ? handleConvertAllBlocksDubbing
                : preProcessSynthesizeDubbing
            }
            isDisableConvertBtn={
              canCreateBlockAudio
                ? isAutoSaveOverLimit ||
                  isEditLocked ||
                  isDubbingConvertBtnDisabled
                : isAutoSaveOverLimit || isSkeletonUI
            }
            isConverting={
              canCreateBlockAudio
                ? isConvertingAllBlocks
                : isProjectSynthesizing
            }
            isPreProcessingSynthesis={
              canCreateBlockAudio ? false : isPreProcessingSynthesis
            }
            isLoading={isLoading}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            handlePlay={handlePlay}
            handlePause={handlePause}
            handleSeeked={handleSeeked}
            title={title}
            blocks={blocks}
            handleChangeVolume={handleChangeVolume}
            onSaveProject={saveProjectBeforeAction}
            synthesisProgress={
              canCreateBlockAudio ? dubbingSynthesisProgress : displayProgress
            }
            isDisabledBtnAudioEffects={isDisabledBtnAudioEffects}
            product={PROJECT_PRODUCT.DUBBING}
            isRewriting={Object.values(filterBlockResults)?.some(
              (block) => block?.rewrite?.status === REWRITE_STATUS.PENDING,
            )}
            showVideoVolumeControl={shouldShowVideoPanel}
          />
        )}

      <NotificationDialogV3
        name="unsaved-changes"
        open={isOpenUnsavedChangesDialog}
        variant="none"
        title={t('unsavedChangesTitle')}
        padding="12px 24px"
        paddingHeader="8px 24px"
        description={t('unsavedChangesDescription')}
        onClose={() => setIsOpenUnsavedChangesDialog(false)}
        actionComponent={renderUnsavedChangesActions()}
      />
      <CustomActionLeavePage
        when={hasUnsavedWork}
        isAutoSave={autoSaveEnabled}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={checkRouteChange}
        visiblePopup={isOpenUnsavedChangesDialog}
        onLeavePage={handleConfirmLeave}
        onCancel={handleCloseUnsavedChangesDialog}
      />
      <CustomActionLeavePage
        isProjectConverting
        when={isProjectConvertingDraft || isConvertingBlockAudio}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={checkRouteChange}
        visiblePopup={isOpenUnsavedChangesDialog}
        onLeavePage={handleConfirmLeave}
        onCancel={handleCloseUnsavedChangesDialog}
      />
      <NotificationDialogV3
        name={openWrongSyntaxDialogV2?.name}
        title={openWrongSyntaxDialogV2?.title}
        description={openWrongSyntaxDialogV2?.description}
        subDescription={openWrongSyntaxDialogV2?.subDescription}
        variant={openWrongSyntaxDialogV2?.variant}
        open={!!openWrongSyntaxDialogV2?.title}
        onClose={() => setOpenWrongSyntaxDialogV2(null)}
        actionComponent={renderCloseButtonV2(() =>
          setOpenWrongSyntaxDialogV2(null),
        )}
      />
      {renderOutOfCreditsDialog()}
      {renderMaxCharsLimitExceededDialog()}
      {openMaxCharsExceededOnSave && (
        <NotificationDialogV4
          open
          onClose={() => setOpenMaxCharsExceededOnSave(false)}
          image={WaringImg}
          title={t('maxCharsLimitExceeded')}
          description={t('pronunciationCheckMaxCharsDesc', {
            maxLengthInputText: delimitNumber(MAX_TOTAL_CHARACTERS, language),
          })}
          actionComponent={
            <Button
              variant="contained"
              onClick={() => setOpenMaxCharsExceededOnSave(false)}
            >
              {t('understood')}
            </Button>
          }
        />
      )}

      {!!openDialogLockedCycle?.title && (
        <NotificationDialogV4
          image={LockCycle}
          title={openDialogLockedCycle?.title}
          description={openDialogLockedCycle?.description}
          actionComponent={renderLockedCycleButton()}
          open={!!openDialogLockedCycle?.title}
          onClose={() => setOpenDialogLockedCycle(null)}
        />
      )}

      <NotificationDialogV3
        name="download-format"
        title={t('noteV2')}
        description={t('desscriptionChangeFormat')}
        variant="warning"
        open={openDialogChangeFormat}
        onClose={() =>
          dispatch(actions.project.setOpenDialogChangeFormat(false))
        }
        actionComponent={renderChangeFormatButton()}
        padding="12px 32px"
      />

      <CommonDialog
        width="512px"
        title={t('confirmDelete')}
        fullWidth
        contentComponent={
          <Typography
            variant="body-16-regular"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t(descriptionDeleteBlockKeyName, {
                value: numberOfBlocksToDelete,
              }),
            }}
          />
        }
        open={deleteDialogData}
        onClose={() => setDeleteDialogData(false)}
        actionComponent={
          <>
            <CommonButton
              customStyle={{
                padding: '8px 24px',
                borderRadius: '8px',
                height: '40px',
              }}
              variant="outlineSmall"
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              onClick={() => setDeleteDialogData(false)}
            >
              {t('cancel')}
            </CommonButton>
            <CommonButton
              customStyle={{
                padding: '8px 24px',
                backgroundColor: COLOR.error,
                borderRadius: '8px',
                height: '40px',
                '&:disabled': {
                  backgroundColor: COLOR.error,
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  pointerEvents: 'auto',
                },
              }}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.white,
              }}
              color="error"
              variant="contained"
              onClick={handleDeleteMultiBlock}
            >
              {t('delete')}
            </CommonButton>
          </>
        }
      />

      <NotificationDialog
        name={openWrongSyntaxDialog?.name}
        title={openWrongSyntaxDialog?.title}
        description={openWrongSyntaxDialog?.description}
        subDescription={openWrongSyntaxDialog?.subDescription}
        variant={openWrongSyntaxDialog?.variant}
        open={!!openWrongSyntaxDialog?.title}
        onClose={() => setOpenWrongSyntaxDialog(null)}
        actionComponent={renderCloseButton(() =>
          setOpenWrongSyntaxDialog(null),
        )}
      />
      <ExpiredPackageAlert
        open={openExpiredPackageDialog}
        onClose={handleCloseExpiredPackageDialog}
      />
      {notifyLowCreditsDialog && (
        <NotifyLowCredits
          open={notifyLowCreditsDialog}
          onClose={() => setNotifyLowCreditsDialog(false)}
          usingPackage={usingPackage}
        />
      )}
      <NotificationDialogV3
        name="confirm-translate-other-language"
        open={openTranslateConfirmDialog}
        variant="none"
        title={t('noteV3')}
        description={translateWarningText}
        onClose={() => {
          setOpenTranslateConfirmDialog(false);
          setTranslateWarningType(null);
        }}
        actionComponent={renderTranslateConfirmActions()}
        padding="12px 24px"
        paddingHeader="8px 24px"
      />

      <NotificationDialogV3
        name="translate-error"
        open={openTranslateErrorDialog}
        width="512px"
        maxWidth="512px"
        widthContent="512px"
        variant="none"
        title={t('errorStatus')}
        description={translateErrorDescription}
        onClose={() => setOpenTranslateErrorDialog(false)}
        actionComponent={renderCloseButton(
          () => setOpenTranslateErrorDialog(false),
          'gotIt',
        )}
        padding="12px 24px"
        paddingHeader="8px 24px"
      />

      <TranslateDialog
        open={openTranslateDialog}
        onClose={() => setOpenTranslateDialog(false)}
        projectId={projectId}
        blocks={blockIds.map((blockId) => blocks[blockId]).filter(Boolean)}
        findBlockWithError={findBlockWithError}
        scrollToBlock={scrollToBlock}
        onSaveProject={saveProjectBeforeAction}
        computeBlockBlackWordsMap={computeBlockBlackWordsMap}
        setBlockBlackWordsMap={setBlockBlackWordsMap}
        onTranslateError={handleOpenTranslateErrorDialog}
        onOpenPopupNotEnoughCredits={handleOpenPopupNotEnoughCredits}
        onTranslateLoadingChange={(showLoading = true, translationId) => {
          translationIdRef.current = translationId || null;
          const nextIsTranslating = Boolean(showLoading);
          setIsTranslating(nextIsTranslating);
          dispatch(actions.project.setIsTranslating(nextIsTranslating));
        }}
      />

      <PopupNotEnoughCredits
        open={openPopupNotEnoughCredits}
        onClose={handleClosePopupNotEnoughCredits}
        projectId={projectId}
      />

      <PopupConfirmCreateAudio
        open={Boolean(confirmConvert)}
        credits={confirmConvertCredits}
        onClose={handleCloseConfirmConvert}
        onConfirm={handleConfirmConvert}
      />

      {isUsingTurnstile && <TurnstileWidget ref={turnstileWidgetRef} />}
      {isTranslating && <AILoadingOverlay text={t('translating')} />}
      <PopupUpPkgUseRewrite
        open={openPopupUpPkgUseRewrite}
        onClose={() => setOpenPopupUpPkgUseRewrite(false)}
        featureKey={FEATURE_KEYS.AI_TRANSLATE}
        surface={MOE_SURFACE.PROJECT_EDITOR}
        product={PROJECT_PRODUCT.DUBBING}
      />
    </>
  );
};

export default Content;
